import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import classNames from "classnames";
import axios from "axios";
import { useQuery } from "react-query";
import ContentHeader from "../../components/Admin/ContentHeader";
import BranchQueueFilter from "../../components/Admin/BranchQueueFilter/BranchQueueFilter";
import BranchQueueTreeSelect from "../../components/Admin/BranchQueueTreeSelect/BranchQueueTreeSelect";
import { Button } from "../../components/Admin/Button/Button";
import TicketConstructor from "../../components/Admin/TicketConstructor/TicketConstructor";
import classes from "./TerminalAppearancePage.module.scss";
import {
  TerminalAppearanceSettings,
  TerminalBackgroundMedia,
  TerminalChildrenModeButtonShape,
  TerminalChildrenModeDecorationType,
  TerminalTicketPictureMedia,
} from "../../types/Admin/TerminalAppearanceTypes";
import {
  CHILDREN_MODE_PRESET_THEMES,
  DEFAULT_CHILDREN_PRESET_THEME_ID,
} from "../../utils/Terminal/childrenModeThemes";
import {
  buildDefaultChildrenModeSettings,
  normalizeChildrenModeSettings,
} from "../../utils/Admin/TerminalAppearance/normalizeChildrenModeForm";
import { parseBoundedIntegerInput } from "../../utils/Admin/TerminalAppearance/parseBoundedIntegerInput";
import {
  TICKET_PAPER_HEIGHT_PX,
  ensureTicketLayout,
  resolveTicketPaperHeightPx,
  syncLegacyTicketFields,
} from "../../utils/Admin/TicketConstructor/ticketLayout";
import { BranchAPI } from "../../constants/API";
import { commonHeaders } from "../../constants/HeadersAPI";
import type { BranchQueueItemDto } from "../../hooks/Admin/WorkingPlacesTable/useGetQueuesForCurrentUser";
import { useEffectiveBranchQueueId } from "../../hooks/Admin/useEffectiveBranchQueueId";
import { useGetTerminalAppearance } from "../../hooks/Admin/TerminalAppearance/useGetTerminalAppearance";
import { usePutTerminalAppearance } from "../../hooks/Admin/TerminalAppearance/usePutTerminalAppearance";
import { usePostTerminalBackground } from "../../hooks/Admin/TerminalAppearance/usePostTerminalBackground";
import { useDeleteTerminalBackground } from "../../hooks/Admin/TerminalAppearance/useDeleteTerminalBackground";
import { usePostTerminalChildrenBackground } from "../../hooks/Admin/TerminalAppearance/usePostTerminalChildrenBackground";
import { useDeleteTerminalChildrenBackground } from "../../hooks/Admin/TerminalAppearance/useDeleteTerminalChildrenBackground";
import { usePostTerminalTicketPicture } from "../../hooks/Admin/TerminalAppearance/usePostTerminalTicketPicture";
import { useDeleteTerminalTicketPicture } from "../../hooks/Admin/TerminalAppearance/useDeleteTerminalTicketPicture";
import { usePostOuterCircleSmall } from "../../hooks/Admin/TerminalAppearance/usePostOuterCircleSmall";
import { usePostSectionOuterCircleSmall } from "../../hooks/Admin/TerminalAppearance/usePostSectionOuterCircleSmall";
import { usePostTerminalLogo } from "../../hooks/Admin/TerminalAppearance/usePostTerminalLogo";
import CustomButton from "../../components/Admin/CustomButton/CustomButton";
import { baseSoundURL } from "../../config";
import terminalTicketIconActiveLarge from "../../assets/icons/terminalTicketIconActiveLarge.svg";
import tehnoSkyTerminalLogo from "../../assets/icons/tehnoSkyTerminalLogo.svg";
import { useMyPermissions } from "../../hooks/useMyPermissions";
import { useContext } from "react";
import { AlertContext } from "../../context/AlertProvider";
import { useAdminQueueIdsFilter } from "../../hooks/Admin/useAdminQueueIdsFilter";
import type { OrderUnitsType } from "../../types/Terminal/TerminalTypes";
import type { BranchQueueLinksDto } from "../BranchesPage/BranchesPage";

const defaultButtonGradient = {
  startColor: "#0066ff",
  endColor: "#56c5f2",
  angle: 180,
};

const fontOptions = [
  { label: "SFPro", value: "SFPro" },
  { label: "Inter", value: "Inter" },
  { label: "Montserrat", value: "Montserrat" },
  { label: "Roboto Regular", value: "Roboto-400" },
  { label: "Roboto Medium", value: "Roboto" },
  { label: "Bebas", value: "Bebas" },
];

const fontWeights = [
  { label: "Regular", value: "regular" },
  { label: "Medium", value: "medium" },
  { label: "Bold", value: "bold" },
];

const normalizeMediaUrl = (url?: string | null) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url) || url.startsWith("//")) {
    return url;
  }
  const separator = url.startsWith("/") ? "" : "/";
  return `${baseSoundURL}${separator}${url}`;
};

const parseGradient = (gradient?: string | null) => {
  if (!gradient) {
    return defaultButtonGradient;
  }

  const angleMatch = gradient.match(/linear-gradient\(([-\d.]+)deg/i);
  const angle = angleMatch
    ? Number(angleMatch[1])
    : defaultButtonGradient.angle;

  const colorMatches =
    gradient.match(/#(?:[0-9a-fA-F]{3}){1,2}|rgba?\([^)]+\)/g) ?? [];
  const [
    startColor = defaultButtonGradient.startColor,
    endColor = defaultButtonGradient.endColor,
  ] = colorMatches;

  return {
    startColor,
    endColor,
    angle: Number.isFinite(angle) ? angle : defaultButtonGradient.angle,
  };
};

const clampNumber = (value: number, min: number, max: number) => {
  return Math.min(max, Math.max(min, value));
};

const normalizeHexColor = (value: string, fallback = "#000000") => {
  const cleaned = value.trim().replace("#", "");
  if (![3, 6].includes(cleaned.length) || !/^[0-9a-fA-F]+$/.test(cleaned)) {
    return fallback;
  }
  let full = cleaned;
  if (cleaned.length === 3) {
    full = cleaned
      .split("")
      .map((char) => char + char)
      .join("");
  }
  return `#${full.toLowerCase()}`;
};

const hexToRgb = (hex: string) => {
  const normalized = normalizeHexColor(hex);
  const raw = normalized.replace("#", "");
  return {
    r: parseInt(raw.slice(0, 2), 16),
    g: parseInt(raw.slice(2, 4), 16),
    b: parseInt(raw.slice(4, 6), 16),
  };
};

const parseColorWithAlpha = (
  value?: string | null,
  fallbackHex = "#000000",
) => {
  if (!value) {
    return { hex: fallbackHex, alpha: 1 };
  }
  const trimmed = value.trim();
  if (trimmed.startsWith("#")) {
    return { hex: normalizeHexColor(trimmed, fallbackHex), alpha: 1 };
  }
  const rgbaMatch = trimmed.match(/rgba?\(([^)]+)\)/i);
  if (rgbaMatch) {
    const parts = rgbaMatch[1].split(",").map((part) => part.trim());
    const r = Number(parts[0]);
    const g = Number(parts[1]);
    const b = Number(parts[2]);
    if (![r, g, b].every((channel) => Number.isFinite(channel))) {
      return { hex: fallbackHex, alpha: 1 };
    }
    const hex = `#${[r, g, b]
      .map((channel) =>
        clampNumber(Math.round(channel), 0, 255).toString(16).padStart(2, "0"),
      )
      .join("")}`;
    const alpha = parts.length > 3 ? clampNumber(Number(parts[3]), 0, 1) : 1;
    return {
      hex: normalizeHexColor(hex, fallbackHex),
      alpha: Number.isFinite(alpha) ? alpha : 1,
    };
  }
  return { hex: fallbackHex, alpha: 1 };
};

const buildColorWithAlpha = (hex: string, alpha: number) => {
  const normalizedHex = normalizeHexColor(hex);
  const clamped = clampNumber(alpha, 0, 1);
  if (clamped >= 1) {
    return normalizedHex;
  }
  const { r, g, b } = hexToRgb(normalizedHex);
  const roundedAlpha = Math.round(clamped * 100) / 100;
  return `rgba(${r}, ${g}, ${b}, ${roundedAlpha})`;
};

const buildGradient = (settings: typeof defaultButtonGradient) =>
  `linear-gradient(${settings.angle}deg, ${settings.startColor} 0%, ${settings.endColor} 100%)`;

const defaultOuterCircleSmallScale = 100;
const defaultOuterCircleSmallPosition = 50;
const defaultOuterCircleSmallFitMode: NonNullable<
  TerminalAppearanceSettings["buttons"]
>["ticketOuterCircleSmallFitMode"] = "scale";
const defaultLogoScale = 100;
const defaultLogoPosition = 50;
const defaultLogoFitMode: NonNullable<
  TerminalAppearanceSettings["logo"]
>["fitMode"] = "scale";
const defaultBackgroundFitMode: NonNullable<
  TerminalAppearanceSettings["backgroundFitMode"]
> = "cover";
const defaultQueueVideoVolume = 20;
const defaultTicketSettings = {
  companyName: "",
  firstLine: "",
  secondLine: "",
  companyNameFontSize: 2,
  firstLineFontSize: 1,
  secondLineFontSize: 1,
  ticketNumberFontSize: 8,
  topicNameFontSize: 3,
  paperHeightPx: TICKET_PAPER_HEIGHT_PX,
};
const defaultTicketWithImageSettings = {
  isEnabled: false,
};
const defaultHeaderSettings = {
  backgroundColor: "#fdfdfd",
  textColor: "#000000",
  fontFamily: "SFPro",
  fontSize: 32,
};
const defaultChildrenModeSettings = buildDefaultChildrenModeSettings(
  defaultBackgroundFitMode,
);
const defaultAutoReturnHomeSettings = {
  idleSeconds: 0,
};

type TerminalSectionKey =
  | "preview"
  | "categoriesGrid"
  | "servicesGrid"
  | "background"
  | "logo"
  | "greeting"
  | "header"
  | "categoryButtons"
  | "serviceButtons"
  | "ticket"
  | "ticketWithImage"
  | "childrenMode"
  | "autoReturnHome"
  | "customCss";

const getMediaBackgroundSize = (
  fitMode:
    | NonNullable<TerminalAppearanceSettings["logo"]>["fitMode"]
    | NonNullable<
        TerminalAppearanceSettings["buttons"]
      >["ticketOuterCircleSmallFitMode"],
  scale: number,
) => {
  switch (fitMode) {
    case "contain":
      return "contain";
    case "cover":
      return "cover";
    case "stretch":
      return "100% 100%";
    case "width":
      return "100% auto";
    case "height":
      return "auto 100%";
    case "none":
      return "auto";
    case "scale":
    default:
      return `${scale}% ${scale}%`;
  }
};

const TerminalAppearancePage = () => {
  const alertContext = useContext(AlertContext);
  const { canEdit } = useMyPermissions();
  const canEditTerminal = canEdit("SETTINGS_TERMINAL");
  const { queueIdsFilter } = useAdminQueueIdsFilter();

  const [filterBranchIds, setFilterBranchIds] = useState<number[]>([]);
  const [filterQueueIds, setFilterQueueIds] = useState<number[]>([]);
  const [saveBindingModalOpen, setSaveBindingModalOpen] = useState(false);
  const [saveModalBranchIds, setSaveModalBranchIds] = useState<number[]>([]);
  const [saveModalQueueIds, setSaveModalQueueIds] = useState<number[]>([]);
  const effectiveBranchQueueId = useEffectiveBranchQueueId(
    filterBranchIds,
    filterQueueIds,
  );
  const firstBranchId = filterBranchIds[0] ?? null;
  const { data: queuesForSelectedBranch = [] } = useQuery<BranchQueueItemDto[]>(
    ["branches", firstBranchId, "queues"] as const,
    () =>
      axios
        .get<BranchQueueItemDto[]>(
          BranchAPI.getQueues(firstBranchId!),
          commonHeaders(),
        )
        .then((r) => r.data),
    { enabled: firstBranchId != null },
  );
  const selectedQueueName =
    queuesForSelectedBranch.find((queue) => queue.id === filterQueueIds[0])
      ?.queueName ?? null;
  const { terminalAppearanceResponse, isLoadingTerminalAppearance } =
    useGetTerminalAppearance({
      branchQueueId: effectiveBranchQueueId,
    });
  const { updateTerminalAppearance, isUpdateTerminalAppearanceLoading } =
    usePutTerminalAppearance();
  const { uploadTerminalBackground, isUploadTerminalBackgroundLoading } =
    usePostTerminalBackground();
  const { deleteTerminalBackground, isDeleteTerminalBackgroundLoading } =
    useDeleteTerminalBackground();
  const {
    uploadTerminalChildrenBackground,
    isUploadTerminalChildrenBackgroundLoading,
  } = usePostTerminalChildrenBackground();
  const {
    deleteTerminalChildrenBackground,
    isDeleteTerminalChildrenBackgroundLoading,
  } = useDeleteTerminalChildrenBackground();
  const { uploadTerminalTicketPicture, isUploadTerminalTicketPictureLoading } =
    usePostTerminalTicketPicture();
  const { deleteTerminalTicketPicture, isDeleteTerminalTicketPictureLoading } =
    useDeleteTerminalTicketPicture();
  const { uploadOuterCircleSmall, isUploadOuterCircleSmallLoading } =
    usePostOuterCircleSmall();
  const {
    uploadSectionOuterCircleSmall,
    isUploadSectionOuterCircleSmallLoading,
  } = usePostSectionOuterCircleSmall();
  const { uploadTerminalLogo, isUploadTerminalLogoLoading } =
    usePostTerminalLogo();
  const [form, setForm] = useState<TerminalAppearanceSettings | null>(null);
  const [backgrounds, setBackgrounds] = useState<TerminalBackgroundMedia[]>([]);
  const [childrenBackgrounds, setChildrenBackgrounds] = useState<
    TerminalBackgroundMedia[]
  >([]);
  const [ticketPictures, setTicketPictures] = useState<
    TerminalTicketPictureMedia[]
  >([]);
  const [ticketLayoutImages, setTicketLayoutImages] = useState<
    TerminalTicketPictureMedia[]
  >([]);
  const [buttonGradient, setButtonGradient] = useState(defaultButtonGradient);
  const [sectionButtonGradient, setSectionButtonGradient] = useState(
    defaultButtonGradient,
  );
  const [applySectionSettingsToButtons, setApplySectionSettingsToButtons] =
    useState(false);
  const applySectionSettingsToButtonsRef = useRef(
    applySectionSettingsToButtons,
  );
  const [defaultForm, setDefaultForm] =
    useState<TerminalAppearanceSettings | null>(null);
  const appearanceSourceKeyRef = useRef<number | null | undefined>(undefined);
  const [sectionButtonGroupsExpanded, setSectionButtonGroupsExpanded] =
    useState({
      icon: false,
      fill: false,
      font: false,
    });
  const [buttonGroupsExpanded, setButtonGroupsExpanded] = useState({
    icon: false,
    fill: false,
    font: false,
  });
  const [sectionsExpanded, setSectionsExpanded] = useState<
    Record<TerminalSectionKey, boolean>
  >({
    preview: true,
    categoriesGrid: false,
    servicesGrid: false,
    background: false,
    logo: false,
    greeting: false,
    header: false,
    categoryButtons: false,
    serviceButtons: false,
    ticket: false,
    ticketWithImage: false,
    childrenMode: false,
    autoReturnHome: false,
    customCss: false,
  });

  const buildFormState = (
    settings: TerminalAppearanceSettings,
  ): TerminalAppearanceSettings => ({
    ...settings,
    grid: { ...settings.grid },
    sectionGrid: { ...(settings.sectionGrid ?? settings.grid) },
    greeting: {
      ...settings.greeting,
      title: { ...settings.greeting.title },
      subTitle: { ...settings.greeting.subTitle },
    },
    buttons: {
      ...settings.buttons,
      ticketOuterCircleSmallUrl:
        settings.buttons?.ticketOuterCircleSmallUrl ?? null,
    },
    sectionButtons: {
      ...(settings.sectionButtons ?? settings.buttons),
      ticketOuterCircleSmallUrl:
        settings.sectionButtons?.ticketOuterCircleSmallUrl ??
        settings.buttons?.ticketOuterCircleSmallUrl ??
        null,
    },
    ticket: {
      ...defaultTicketSettings,
      ...(settings.ticket ?? {}),
    },
    ticketWithImage: {
      ...defaultTicketWithImageSettings,
      ...(settings.ticketWithImage ?? {}),
    },
    header: {
      ...defaultHeaderSettings,
      ...(settings.header ?? {}),
    },
    videoVolume: Number.isFinite(settings.videoVolume)
      ? settings.videoVolume
      : defaultQueueVideoVolume,
    logo: {
      url: settings.logo?.url ?? null,
      fitMode: settings.logo?.fitMode ?? defaultLogoFitMode,
      scale: settings.logo?.scale ?? defaultLogoScale,
      positionX: settings.logo?.positionX ?? defaultLogoPosition,
      positionY: settings.logo?.positionY ?? defaultLogoPosition,
    },
    backgroundFitMode: settings.backgroundFitMode ?? defaultBackgroundFitMode,
    backgroundOffsetByHeader: settings.backgroundOffsetByHeader ?? false,
    childrenMode: {
      ...defaultChildrenModeSettings,
      ...(settings.childrenMode ?? {}),
      decorationType: settings.childrenMode?.decorationType ?? "THEME",
      presetThemeId:
        settings.childrenMode?.presetThemeId ??
        DEFAULT_CHILDREN_PRESET_THEME_ID,
      backgroundFitMode:
        settings.childrenMode?.backgroundFitMode ?? defaultBackgroundFitMode,
      buttonShape: settings.childrenMode?.buttonShape ?? "RECTANGLE",
    },
    autoReturnHome: {
      ...defaultAutoReturnHomeSettings,
      ...(settings.autoReturnHome ?? {}),
      idleSeconds: Number.isFinite(settings.autoReturnHome?.idleSeconds)
        ? Math.max(0, settings.autoReturnHome!.idleSeconds!)
        : defaultAutoReturnHomeSettings.idleSeconds,
    },
  });

  const parseNumberInput = (value: string) => {
    if (!value.trim()) return Number.NaN;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : Number.NaN;
  };

  const toNumberInputValue = (value?: number | null): string | number =>
    typeof value === "number" && Number.isFinite(value) ? value : "";

  const toFiniteNumber = (
    value: number | null | undefined,
    fallback: number,
  ): number =>
    typeof value === "number" && Number.isFinite(value) ? value : fallback;

  useEffect(() => {
    if (!terminalAppearanceResponse?.settings) return;

    const nextBackgrounds = terminalAppearanceResponse.backgrounds || [];
    const nextChildrenBackgrounds =
      terminalAppearanceResponse.childrenBackgrounds || [];
    const nextTicketPictures = terminalAppearanceResponse.ticketPictures || [];
    const nextTicketLayoutImages =
      terminalAppearanceResponse.ticketLayoutImages || [];
    const settings = buildFormState(terminalAppearanceResponse.settings);
    const sourceChanged =
      appearanceSourceKeyRef.current !== effectiveBranchQueueId;

    appearanceSourceKeyRef.current = effectiveBranchQueueId;

    if (sourceChanged) {
      // Первая загрузка или смена очереди — берём настройки с сервера.
      setForm(settings);
      setDefaultForm(settings);
    } else {
      // Обновление медиа (загрузка/удаление фона и т.п.) — не сбрасываем
      // локальные правки формы (в т.ч. decorationType = INDIVIDUAL).
      setForm((current) => {
        if (!current) return settings;

        const activeBackgroundStillExists = nextBackgrounds.some(
          (background) => background.id === current.activeBackgroundId,
        );
        const activeChildrenBackgroundStillExists =
          nextChildrenBackgrounds.some(
            (background) =>
              background.id === current.childrenMode?.activeBackgroundId,
          );

        return {
          ...current,
          activeBackgroundId: activeBackgroundStillExists
            ? current.activeBackgroundId
            : null,
          childrenMode: {
            ...defaultChildrenModeSettings,
            ...(current.childrenMode ?? {}),
            activeBackgroundId: activeChildrenBackgroundStillExists
              ? (current.childrenMode?.activeBackgroundId ?? null)
              : null,
          },
        };
      });
    }

    setBackgrounds(nextBackgrounds);
    setChildrenBackgrounds(nextChildrenBackgrounds);
    setTicketPictures(nextTicketPictures);
    setTicketLayoutImages(nextTicketLayoutImages);
  }, [terminalAppearanceResponse, effectiveBranchQueueId]);

  useEffect(() => {
    if (!form?.buttons?.gradient) return;
    setButtonGradient(parseGradient(form.buttons.gradient));
  }, [form?.buttons?.gradient]);

  useEffect(() => {
    if (!form?.sectionButtons?.gradient) return;
    setSectionButtonGradient(parseGradient(form.sectionButtons.gradient));
  }, [form?.sectionButtons?.gradient]);

  useEffect(() => {
    applySectionSettingsToButtonsRef.current = applySectionSettingsToButtons;
  }, [applySectionSettingsToButtons]);

  const toggleSectionExpanded = (key: TerminalSectionKey) => {
    setSectionsExpanded((prev) => {
      const isExpanding = !prev[key];
      const next = { ...prev, [key]: isExpanding };

      if (key === "childrenMode" && isExpanding) {
        next.preview = true;
      }

      return next;
    });
  };

  const isChildrenModePreviewActive = useMemo(() => {
    if (!form) return false;
    if (form.childrenMode?.activateChildrenMode) return true;
    return sectionsExpanded.childrenMode;
  }, [form, sectionsExpanded.childrenMode]);

  const toggleSectionButtonGroup = (
    key: keyof typeof sectionButtonGroupsExpanded,
  ) => {
    setSectionButtonGroupsExpanded((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const toggleButtonGroup = (key: keyof typeof buttonGroupsExpanded) => {
    setButtonGroupsExpanded((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const activeBackgroundId = useMemo(() => {
    if (form?.activeBackgroundId) {
      return form.activeBackgroundId;
    }
    return backgrounds.find((background) => background.isActive)?.id ?? null;
  }, [form?.activeBackgroundId, backgrounds]);

  const activeChildrenBackgroundId = useMemo(() => {
    return form?.childrenMode?.activeBackgroundId ?? null;
  }, [form?.childrenMode?.activeBackgroundId]);

  const updateGrid = (
    key: keyof TerminalAppearanceSettings["grid"],
    value: number,
  ) => {
    if (!form) return;
    const nextValue =
      key === "columns" && Number.isFinite(value) ? Math.max(1, value) : value;
    setForm({
      ...form,
      grid: {
        ...form.grid,
        [key]: nextValue,
      },
    });
  };

  const updateSectionGrid = (
    key: keyof TerminalAppearanceSettings["grid"],
    value: number,
  ) => {
    if (!form) return;
    const nextValue =
      key === "columns" && Number.isFinite(value) ? Math.max(1, value) : value;
    setForm({
      ...form,
      sectionGrid: {
        ...(form.sectionGrid ?? form.grid),
        [key]: nextValue,
      },
    });
  };

  const updateGridRange = (
    minKey: "minCardHeight" | "minCardWidth",
    maxKey: "maxCardHeight" | "maxCardWidth",
    value: number,
  ) => {
    if (!form) return;
    setForm({
      ...form,
      grid: {
        ...form.grid,
        [minKey]: value,
        [maxKey]: value,
      },
    });
  };

  const updateSectionGridRange = (
    minKey: "minCardHeight" | "minCardWidth",
    maxKey: "maxCardHeight" | "maxCardWidth",
    value: number,
  ) => {
    if (!form) return;
    setForm({
      ...form,
      sectionGrid: {
        ...(form.sectionGrid ?? form.grid),
        [minKey]: value,
        [maxKey]: value,
      },
    });
  };

  const cardHeightValue = toNumberInputValue(
    Number.isFinite(form?.grid?.minCardHeight)
      ? form?.grid?.minCardHeight
      : form?.grid?.maxCardHeight,
  );
  const cardWidthValue = toNumberInputValue(
    Number.isFinite(form?.grid?.minCardWidth)
      ? form?.grid?.minCardWidth
      : form?.grid?.maxCardWidth,
  );
  const sectionCardHeightValue = toNumberInputValue(
    Number.isFinite(form?.sectionGrid?.minCardHeight)
      ? form?.sectionGrid?.minCardHeight
      : form?.sectionGrid?.maxCardHeight,
  );
  const sectionCardWidthValue = toNumberInputValue(
    Number.isFinite(form?.sectionGrid?.minCardWidth)
      ? form?.sectionGrid?.minCardWidth
      : form?.sectionGrid?.maxCardWidth,
  );

  const updateGreetingText = (
    key: "title" | "subTitle",
    lang: "ru" | "by" | "en",
    value: string,
  ) => {
    if (!form) return;
    setForm({
      ...form,
      greeting: {
        ...form.greeting,
        [key]: {
          ...form.greeting[key],
          [lang]: value,
        },
      },
    });
  };

  const updateGreetingSetting = (
    key: keyof Pick<
      TerminalAppearanceSettings["greeting"],
      "fontSize" | "paddingTop" | "paddingBottom"
    >,
    value: number,
  ) => {
    if (!form) return;
    setForm({
      ...form,
      greeting: {
        ...form.greeting,
        [key]: value,
      },
    });
  };

  const updateGreetingColor = (value: string) => {
    if (!form) return;
    setForm({
      ...form,
      greeting: {
        ...form.greeting,
        textColor: value,
      },
    });
  };

  const updateGreetingFlag = (
    key: keyof Pick<TerminalAppearanceSettings["greeting"], "isEnabled">,
    value: boolean,
  ) => {
    if (!form) return;
    setForm({
      ...form,
      greeting: {
        ...form.greeting,
        [key]: value,
      },
    });
  };

  const updateHeaderSetting = (
    key: keyof NonNullable<TerminalAppearanceSettings["header"]>,
    value: string | number,
  ) => {
    if (!form) return;
    setForm({
      ...form,
      header: {
        ...(form.header ?? defaultHeaderSettings),
        [key]: value,
      },
    });
  };

  const updateButtonSettings = (
    key: keyof TerminalAppearanceSettings["buttons"],
    value: string | boolean | number,
  ) => {
    if (!form) return;
    setForm({
      ...form,
      buttons: {
        ...form.buttons,
        [key]: value,
      },
    });
  };

  const updateSectionButtonSettings = (
    key: keyof NonNullable<TerminalAppearanceSettings["sectionButtons"]>,
    value: string | boolean | number,
  ) => {
    if (!form) return;
    setForm((current) => {
      if (!current) return current;
      const nextSectionButtons = {
        ...(current.sectionButtons ?? current.buttons),
        [key]: value,
      };
      const nextForm = {
        ...current,
        sectionButtons: nextSectionButtons,
      };
      if (applySectionSettingsToButtonsRef.current) {
        return {
          ...nextForm,
          buttons: {
            ...current.buttons,
            ...nextSectionButtons,
          },
        };
      }
      return nextForm;
    });
  };

  const applySectionButtonsToServices = () => {
    if (!form) return;
    setForm((current) => {
      if (!current) return current;
      const nextSectionButtons = (current.sectionButtons ??
        current.buttons) as TerminalAppearanceSettings["buttons"];
      return {
        ...current,
        buttons: {
          ...current.buttons,
          ...nextSectionButtons,
        },
      };
    });
  };

  const updateLogoSettings = (
    key: "url" | "scale" | "positionX" | "positionY" | "fitMode",
    value: string | number | null,
  ) => {
    if (!form) return;
    setForm({
      ...form,
      logo: {
        ...(form.logo ?? {
          url: null,
          fitMode: defaultLogoFitMode,
          scale: defaultLogoScale,
          positionX: defaultLogoPosition,
          positionY: defaultLogoPosition,
        }),
        [key]: value,
      },
    });
  };

  const updateButtonGradient = (
    partial: Partial<typeof defaultButtonGradient>,
  ) => {
    if (!form) return;
    setButtonGradient((current) => {
      const next = { ...current, ...partial };
      updateButtonSettings("gradient", buildGradient(next));
      return next;
    });
  };

  const updateSectionButtonGradient = (
    partial: Partial<typeof defaultButtonGradient>,
  ) => {
    if (!form) return;
    setSectionButtonGradient((current) => {
      const next = { ...current, ...partial };
      updateSectionButtonSettings("gradient", buildGradient(next));
      return next;
    });
  };

  const handleSave = () => {
    if (!form) return;
    const normalizeGrid = (
      grid: TerminalAppearanceSettings["grid"],
      fallback?: TerminalAppearanceSettings["grid"],
    ) => ({
      ...grid,
      columns: Number.isFinite(grid.columns)
        ? Math.max(1, grid.columns)
        : (fallback?.columns ?? 1),
      gap: Number.isFinite(grid.gap) ? grid.gap : (fallback?.gap ?? 0),
      rowGap: Number.isFinite(grid.rowGap)
        ? grid.rowGap
        : (fallback?.rowGap ?? 0),
      minCardHeight: Number.isFinite(grid.minCardHeight)
        ? grid.minCardHeight
        : (fallback?.minCardHeight ?? 0),
      maxCardHeight: Number.isFinite(grid.maxCardHeight)
        ? grid.maxCardHeight
        : (fallback?.maxCardHeight ?? 0),
      minCardWidth: Number.isFinite(grid.minCardWidth)
        ? grid.minCardWidth
        : (fallback?.minCardWidth ?? 0),
      maxCardWidth: Number.isFinite(grid.maxCardWidth)
        ? grid.maxCardWidth
        : (fallback?.maxCardWidth ?? 0),
      paddingTop: Number.isFinite(grid.paddingTop)
        ? grid.paddingTop
        : (fallback?.paddingTop ?? 0),
      paddingX: Number.isFinite(grid.paddingX)
        ? grid.paddingX
        : (fallback?.paddingX ?? 0),
    });
    const normalizedGrid = normalizeGrid(form.grid, defaultForm?.grid);
    const normalizedSectionGrid = normalizeGrid(
      form.sectionGrid ?? form.grid,
      defaultForm?.sectionGrid ?? defaultForm?.grid,
    );
    const normalizedGreeting = {
      ...form.greeting,
      fontSize: Number.isFinite(form.greeting.fontSize)
        ? form.greeting.fontSize
        : 0,
      paddingTop: Number.isFinite(form.greeting.paddingTop)
        ? form.greeting.paddingTop
        : 0,
      paddingBottom: Number.isFinite(form.greeting.paddingBottom)
        ? form.greeting.paddingBottom
        : 0,
      isEnabled:
        typeof form.greeting.isEnabled === "boolean"
          ? form.greeting.isEnabled
          : true,
      textColor: form.greeting.textColor || "#000000",
    };
    const normalizeButtons = (
      buttons: TerminalAppearanceSettings["buttons"],
      fallback?: TerminalAppearanceSettings["buttons"],
    ) => ({
      ...buttons,
      ticketOuterCircleSmallFitMode:
        buttons.ticketOuterCircleSmallFitMode ??
        fallback?.ticketOuterCircleSmallFitMode ??
        defaultOuterCircleSmallFitMode,
      ticketOuterCircleSmallScale: Number.isFinite(
        buttons.ticketOuterCircleSmallScale,
      )
        ? buttons.ticketOuterCircleSmallScale
        : (fallback?.ticketOuterCircleSmallScale ??
          defaultOuterCircleSmallScale),
      ticketOuterCircleSmallPositionX: Number.isFinite(
        buttons.ticketOuterCircleSmallPositionX,
      )
        ? buttons.ticketOuterCircleSmallPositionX
        : (fallback?.ticketOuterCircleSmallPositionX ??
          defaultOuterCircleSmallPosition),
      ticketOuterCircleSmallPositionY: Number.isFinite(
        buttons.ticketOuterCircleSmallPositionY,
      )
        ? buttons.ticketOuterCircleSmallPositionY
        : (fallback?.ticketOuterCircleSmallPositionY ??
          defaultOuterCircleSmallPosition),
    });
    const normalizedButtons = normalizeButtons(
      form.buttons,
      defaultForm?.buttons,
    );
    const normalizedSectionButtons = normalizeButtons(
      (form.sectionButtons ??
        form.buttons) as TerminalAppearanceSettings["buttons"],
      (defaultForm?.sectionButtons ??
        defaultForm?.buttons) as TerminalAppearanceSettings["buttons"],
    );
    const finalButtons = applySectionSettingsToButtons
      ? {
          ...normalizedButtons,
          ...normalizedSectionButtons,
        }
      : normalizedButtons;
    const normalizedTicket = syncLegacyTicketFields(
      {
        ...form.ticket,
        companyName: form.ticket.companyName ?? "",
        firstLine: form.ticket.firstLine ?? "",
        secondLine: form.ticket.secondLine ?? "",
        companyNameFontSize: Number.isFinite(form.ticket.companyNameFontSize)
          ? form.ticket.companyNameFontSize
          : defaultTicketSettings.companyNameFontSize,
        firstLineFontSize: Number.isFinite(form.ticket.firstLineFontSize)
          ? form.ticket.firstLineFontSize
          : defaultTicketSettings.firstLineFontSize,
        secondLineFontSize: Number.isFinite(form.ticket.secondLineFontSize)
          ? form.ticket.secondLineFontSize
          : defaultTicketSettings.secondLineFontSize,
        ticketNumberFontSize: Number.isFinite(form.ticket.ticketNumberFontSize)
          ? form.ticket.ticketNumberFontSize
          : defaultTicketSettings.ticketNumberFontSize,
        topicNameFontSize: Number.isFinite(form.ticket.topicNameFontSize)
          ? form.ticket.topicNameFontSize
          : defaultTicketSettings.topicNameFontSize,
        paperHeightPx: resolveTicketPaperHeightPx(form.ticket),
      },
      ensureTicketLayout(form.ticket),
    );
    const normalizedTicketWithImage = {
      isEnabled: form.ticketWithImage?.isEnabled ?? false,
    };
    const normalizedHeader = {
      backgroundColor:
        form.header?.backgroundColor ?? defaultHeaderSettings.backgroundColor,
      textColor: form.header?.textColor ?? defaultHeaderSettings.textColor,
      fontFamily: form.header?.fontFamily ?? defaultHeaderSettings.fontFamily,
      fontSize: Number.isFinite(form.header?.fontSize)
        ? (form.header?.fontSize as number)
        : defaultHeaderSettings.fontSize,
    };
    const normalizedVideoVolume = Number.isFinite(form.videoVolume)
      ? Math.min(100, Math.max(0, form.videoVolume))
      : defaultQueueVideoVolume;
    const normalizedLogo = {
      url: form.logo?.url ?? null,
      fitMode: form.logo?.fitMode ?? defaultLogoFitMode,
      scale: Number.isFinite(form.logo?.scale)
        ? form.logo?.scale
        : defaultLogoScale,
      positionX: Number.isFinite(form.logo?.positionX)
        ? form.logo?.positionX
        : defaultLogoPosition,
      positionY: Number.isFinite(form.logo?.positionY)
        ? form.logo?.positionY
        : defaultLogoPosition,
    };
    const normalizedBackgroundFitMode =
      form.backgroundFitMode ?? defaultBackgroundFitMode;
    const normalizedBackgroundOffsetByHeader =
      typeof form.backgroundOffsetByHeader === "boolean"
        ? form.backgroundOffsetByHeader
        : (defaultForm?.backgroundOffsetByHeader ?? false);
    const normalizedChildrenMode = normalizeChildrenModeSettings({
      childrenMode: form.childrenMode,
      activeChildrenBackgroundId,
      defaultBackgroundFitMode,
    });
    const normalizedAutoReturnHome = {
      idleSeconds: Number.isFinite(form.autoReturnHome?.idleSeconds)
        ? Math.max(0, form.autoReturnHome!.idleSeconds!)
        : defaultAutoReturnHomeSettings.idleSeconds,
    };
    updateTerminalAppearance({
      ...form,
      grid: normalizedGrid,
      sectionGrid: normalizedSectionGrid,
      greeting: normalizedGreeting,
      buttons: finalButtons,
      sectionButtons: normalizedSectionButtons,
      ticket: normalizedTicket,
      ticketWithImage: normalizedTicketWithImage,
      header: normalizedHeader,
      logo: normalizedLogo,
      videoVolume: normalizedVideoVolume,
      backgroundFitMode: normalizedBackgroundFitMode,
      backgroundOffsetByHeader: normalizedBackgroundOffsetByHeader,
      activeBackgroundId: activeBackgroundId,
      childrenMode: normalizedChildrenMode,
      autoReturnHome: normalizedAutoReturnHome,
    });
  };

  const handleSaveClick = () => {
    if (!canEditTerminal) {
      alertContext?.setAlert("Недостаточно прав для редактирования настроек.");
      return;
    }
    if (!form) return;
    setSaveModalBranchIds(filterBranchIds);
    setSaveModalQueueIds(filterQueueIds);
    setSaveBindingModalOpen(true);
  };

  const handleSaveBindingConfirm = async () => {
    if (!canEditTerminal) {
      alertContext?.setAlert("Недостаточно прав для редактирования настроек.");
      return;
    }
    if (!form) return;
    const queueIdsToSave: number[] = [...saveModalQueueIds];
    if (queueIdsToSave.length === 0) {
      alertContext?.setAlert(
        "Выберите хотя бы одну очередь, чтобы применить настройки только к отмеченным очередям.",
      );
      return;
    }

    // Валидация ориентации терминалов: редактируемый терминал (превью) должен совпадать
    // по ориентации с выбранными терминалами при сохранении, иначе показываем сообщение как на странице Табло.
    if (previewTerminalOrientation) {
      try {
        // Сопоставляем queueId -> branchId, потому что эндпоинт links требует оба параметра
        const queuesByBranch = await Promise.all(
          saveModalBranchIds.map((branchId) =>
            axios
              .get<
                BranchQueueItemDto[]
              >(BranchAPI.getQueues(branchId), commonHeaders())
              .then((r) => ({ branchId, queues: r.data })),
          ),
        );
        const queueIdToBranchId = new Map<number, number>();
        queuesByBranch.forEach(({ branchId, queues }) => {
          queues.forEach((q) => {
            if (q?.id != null) queueIdToBranchId.set(q.id, branchId);
          });
        });

        const selectedOrientations = new Set<"VERTICAL" | "HORIZONTAL">();
        await Promise.all(
          queueIdsToSave.map(async (queueId) => {
            const branchId = queueIdToBranchId.get(queueId);
            if (!branchId) return;
            const links = await axios.get<BranchQueueLinksDto>(
              BranchAPI.queueLinks(branchId, queueId),
              commonHeaders(),
            );
            (links.data?.terminals ?? []).forEach((t) => {
              const o = (t as any)?.terminalOrientation as
                | "VERTICAL"
                | "HORIZONTAL"
                | null
                | undefined;
              if (o) selectedOrientations.add(o);
            });
          }),
        );

        // Если среди выбранных терминалов разные ориентации — просим выбрать одну
        if (selectedOrientations.size > 1) {
          alertContext?.setAlert(
            "Среди выбранных терминалов есть терминалы с разной ориентацией. Выберите терминалы с одной ориентацией.",
          );
          return;
        }

        // Если ориентация выбранных терминалов известна и не совпадает с редактируемым — блокируем сохранение
        const selectedOrientation = Array.from(selectedOrientations)[0] ?? null;
        if (
          selectedOrientation &&
          selectedOrientation !== previewTerminalOrientation
        ) {
          alertContext?.setAlert(
            "Ориентация выбранных терминалов не совпадает с ориентацией редактируемого терминала. Выберите терминалы с подходящей ориентацией.",
          );
          return;
        }
      } catch {
        // Если не смогли получить ориентации — не блокируем сохранение (как минимум не ухудшаем UX),
        // но и не показываем ложную валидацию.
      }
    }

    const normalizeGrid = (
      grid: TerminalAppearanceSettings["grid"],
      fallback?: TerminalAppearanceSettings["grid"],
    ) => ({
      ...grid,
      columns: Number.isFinite(grid.columns)
        ? Math.max(1, grid.columns)
        : (fallback?.columns ?? 1),
      gap: Number.isFinite(grid.gap) ? grid.gap : (fallback?.gap ?? 0),
      rowGap: Number.isFinite(grid.rowGap)
        ? grid.rowGap
        : (fallback?.rowGap ?? 0),
      minCardHeight: Number.isFinite(grid.minCardHeight)
        ? grid.minCardHeight
        : (fallback?.minCardHeight ?? 0),
      maxCardHeight: Number.isFinite(grid.maxCardHeight)
        ? grid.maxCardHeight
        : (fallback?.maxCardHeight ?? 0),
      minCardWidth: Number.isFinite(grid.minCardWidth)
        ? grid.minCardWidth
        : (fallback?.minCardWidth ?? 0),
      maxCardWidth: Number.isFinite(grid.maxCardWidth)
        ? grid.maxCardWidth
        : (fallback?.maxCardWidth ?? 0),
      paddingTop: Number.isFinite(grid.paddingTop)
        ? grid.paddingTop
        : (fallback?.paddingTop ?? 0),
      paddingX: Number.isFinite(grid.paddingX)
        ? grid.paddingX
        : (fallback?.paddingX ?? 0),
    });
    const normalizedGrid = normalizeGrid(form.grid, defaultForm?.grid);
    const normalizedSectionGrid = normalizeGrid(
      form.sectionGrid ?? form.grid,
      defaultForm?.sectionGrid ?? defaultForm?.grid,
    );
    const normalizedGreeting = {
      ...form.greeting,
      fontSize: Number.isFinite(form.greeting.fontSize)
        ? form.greeting.fontSize
        : 0,
      paddingTop: Number.isFinite(form.greeting.paddingTop)
        ? form.greeting.paddingTop
        : 0,
      paddingBottom: Number.isFinite(form.greeting.paddingBottom)
        ? form.greeting.paddingBottom
        : 0,
      isEnabled:
        typeof form.greeting.isEnabled === "boolean"
          ? form.greeting.isEnabled
          : true,
      textColor: form.greeting.textColor || "#000000",
    };
    const normalizeButtons = (
      buttons: TerminalAppearanceSettings["buttons"],
      fallback?: TerminalAppearanceSettings["buttons"],
    ) => ({
      ...buttons,
      ticketOuterCircleSmallFitMode:
        buttons.ticketOuterCircleSmallFitMode ??
        fallback?.ticketOuterCircleSmallFitMode ??
        defaultOuterCircleSmallFitMode,
      ticketOuterCircleSmallScale: Number.isFinite(
        buttons.ticketOuterCircleSmallScale,
      )
        ? buttons.ticketOuterCircleSmallScale
        : (fallback?.ticketOuterCircleSmallScale ??
          defaultOuterCircleSmallScale),
      ticketOuterCircleSmallPositionX: Number.isFinite(
        buttons.ticketOuterCircleSmallPositionX,
      )
        ? buttons.ticketOuterCircleSmallPositionX
        : (fallback?.ticketOuterCircleSmallPositionX ??
          defaultOuterCircleSmallPosition),
      ticketOuterCircleSmallPositionY: Number.isFinite(
        buttons.ticketOuterCircleSmallPositionY,
      )
        ? buttons.ticketOuterCircleSmallPositionY
        : (fallback?.ticketOuterCircleSmallPositionY ??
          defaultOuterCircleSmallPosition),
    });
    const normalizedButtons = normalizeButtons(
      form.buttons,
      defaultForm?.buttons,
    );
    const normalizedSectionButtons = normalizeButtons(
      (form.sectionButtons ??
        form.buttons) as TerminalAppearanceSettings["buttons"],
      (defaultForm?.sectionButtons ??
        defaultForm?.buttons) as TerminalAppearanceSettings["buttons"],
    );
    const finalButtons = applySectionSettingsToButtons
      ? { ...normalizedButtons, ...normalizedSectionButtons }
      : normalizedButtons;
    const normalizedTicket = syncLegacyTicketFields(
      {
        ...form.ticket,
        companyName: form.ticket.companyName ?? "",
        firstLine: form.ticket.firstLine ?? "",
        secondLine: form.ticket.secondLine ?? "",
        companyNameFontSize: Number.isFinite(form.ticket.companyNameFontSize)
          ? form.ticket.companyNameFontSize
          : defaultTicketSettings.companyNameFontSize,
        firstLineFontSize: Number.isFinite(form.ticket.firstLineFontSize)
          ? form.ticket.firstLineFontSize
          : defaultTicketSettings.firstLineFontSize,
        secondLineFontSize: Number.isFinite(form.ticket.secondLineFontSize)
          ? form.ticket.secondLineFontSize
          : defaultTicketSettings.secondLineFontSize,
        ticketNumberFontSize: Number.isFinite(form.ticket.ticketNumberFontSize)
          ? form.ticket.ticketNumberFontSize
          : defaultTicketSettings.ticketNumberFontSize,
        topicNameFontSize: Number.isFinite(form.ticket.topicNameFontSize)
          ? form.ticket.topicNameFontSize
          : defaultTicketSettings.topicNameFontSize,
        paperHeightPx: resolveTicketPaperHeightPx(form.ticket),
      },
      ensureTicketLayout(form.ticket),
    );
    const normalizedTicketWithImage = {
      isEnabled: form.ticketWithImage?.isEnabled ?? false,
    };
    const normalizedHeader = {
      backgroundColor:
        form.header?.backgroundColor ?? defaultHeaderSettings.backgroundColor,
      textColor: form.header?.textColor ?? defaultHeaderSettings.textColor,
      fontFamily: form.header?.fontFamily ?? defaultHeaderSettings.fontFamily,
      fontSize: Number.isFinite(form.header?.fontSize)
        ? (form.header?.fontSize as number)
        : defaultHeaderSettings.fontSize,
    };
    const normalizedVideoVolume = Number.isFinite(form.videoVolume)
      ? Math.min(100, Math.max(0, form.videoVolume))
      : defaultQueueVideoVolume;
    const normalizedLogo = {
      url: form.logo?.url ?? null,
      fitMode: form.logo?.fitMode ?? defaultLogoFitMode,
      scale: Number.isFinite(form.logo?.scale)
        ? form.logo?.scale
        : defaultLogoScale,
      positionX: Number.isFinite(form.logo?.positionX)
        ? form.logo?.positionX
        : defaultLogoPosition,
      positionY: Number.isFinite(form.logo?.positionY)
        ? form.logo?.positionY
        : defaultLogoPosition,
    };
    const normalizedBackgroundFitMode =
      form.backgroundFitMode ?? defaultBackgroundFitMode;
    const normalizedBackgroundOffsetByHeader =
      typeof form.backgroundOffsetByHeader === "boolean"
        ? form.backgroundOffsetByHeader
        : (defaultForm?.backgroundOffsetByHeader ?? false);
    const normalizedChildrenMode = normalizeChildrenModeSettings({
      childrenMode: form.childrenMode,
      activeChildrenBackgroundId,
      defaultBackgroundFitMode,
    });
    const normalizedAutoReturnHome = {
      idleSeconds: Number.isFinite(form.autoReturnHome?.idleSeconds)
        ? Math.max(0, form.autoReturnHome!.idleSeconds!)
        : defaultAutoReturnHomeSettings.idleSeconds,
    };
    updateTerminalAppearance({
      ...form,
      grid: normalizedGrid,
      sectionGrid: normalizedSectionGrid,
      greeting: normalizedGreeting,
      buttons: finalButtons,
      sectionButtons: normalizedSectionButtons,
      ticket: normalizedTicket,
      ticketWithImage: normalizedTicketWithImage,
      header: normalizedHeader,
      logo: normalizedLogo,
      videoVolume: normalizedVideoVolume,
      backgroundFitMode: normalizedBackgroundFitMode,
      backgroundOffsetByHeader: normalizedBackgroundOffsetByHeader,
      activeBackgroundId: activeBackgroundId,
      childrenMode: normalizedChildrenMode,
      autoReturnHome: normalizedAutoReturnHome,
      queueIds: queueIdsToSave.length > 0 ? queueIdsToSave : undefined,
    });
    setSaveBindingModalOpen(false);
  };

  const handleUploadBackground = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!canEditTerminal) {
      alertContext?.setAlert("Недостаточно прав для редактирования настроек.");
      event.target.value = "";
      return;
    }
    if (!event.target.files?.length) return;
    uploadTerminalBackground(event.target.files[0]);
    event.target.value = "";
  };

  const handleUploadChildrenBackground = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!canEditTerminal) {
      alertContext?.setAlert("Недостаточно прав для редактирования настроек.");
      event.target.value = "";
      return;
    }
    if (!event.target.files?.length) return;
    uploadTerminalChildrenBackground(event.target.files[0]);
    event.target.value = "";
  };

  const handleUploadTicketPicture = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!canEditTerminal) {
      alertContext?.setAlert("Недостаточно прав для редактирования настроек.");
      event.target.value = "";
      return;
    }
    if (!event.target.files?.length) return;
    uploadTerminalTicketPicture(event.target.files[0]);
    event.target.value = "";
  };

  const handleUploadOuterCircleSmall = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!canEditTerminal) {
      alertContext?.setAlert("Недостаточно прав для редактирования настроек.");
      event.target.value = "";
      return;
    }
    if (!event.target.files?.length) return;
    uploadOuterCircleSmall(event.target.files[0], {
      onSuccess: (response) => {
        const url = response?.data?.url ?? "";
        setForm((current) =>
          current
            ? {
                ...current,
                buttons: {
                  ...current.buttons,
                  ticketOuterCircleSmallUrl: url,
                },
              }
            : current,
        );
      },
    });
    event.target.value = "";
  };

  const handleUploadSectionOuterCircleSmall = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!canEditTerminal) {
      alertContext?.setAlert("Недостаточно прав для редактирования настроек.");
      event.target.value = "";
      return;
    }
    if (!event.target.files?.length) return;
    uploadSectionOuterCircleSmall(event.target.files[0], {
      onSuccess: (response) => {
        const url = response?.data?.url ?? "";
        setForm((current) => {
          if (!current) return current;
          const nextSectionButtons = {
            ...(current.sectionButtons ?? current.buttons),
            ticketOuterCircleSmallUrl: url,
          };
          return {
            ...current,
            sectionButtons: nextSectionButtons,
            ...(applySectionSettingsToButtonsRef.current
              ? {
                  buttons: {
                    ...current.buttons,
                    ...nextSectionButtons,
                  },
                }
              : {}),
          };
        });
      },
    });
    event.target.value = "";
  };

  const handleUploadLogo = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!canEditTerminal) {
      alertContext?.setAlert("Недостаточно прав для редактирования настроек.");
      event.target.value = "";
      return;
    }
    if (!event.target.files?.length) return;
    uploadTerminalLogo(event.target.files[0], {
      onSuccess: (response) => {
        const url = response?.data?.url ?? "";
        updateLogoSettings("url", url);
      },
    });
    event.target.value = "";
  };

  const handleReset = () => {
    if (!defaultForm) return;
    const nextForm = buildFormState(defaultForm);
    const nextFormWithSync = applySectionSettingsToButtons
      ? {
          ...nextForm,
          buttons: {
            ...nextForm.buttons,
            ...(nextForm.sectionButtons ?? nextForm.buttons),
          },
        }
      : nextForm;
    setForm(nextFormWithSync);
    setButtonGradient(parseGradient(nextFormWithSync.buttons.gradient));
    setSectionButtonGradient(
      parseGradient(
        nextFormWithSync.sectionButtons?.gradient ??
          nextFormWithSync.buttons.gradient,
      ),
    );
  };

  const handleRemoveOuterCircleSmall = () => {
    setForm((current) =>
      current
        ? {
            ...current,
            buttons: {
              ...current.buttons,
              ticketOuterCircleSmallUrl: null,
            },
          }
        : current,
    );
  };

  const handleRemoveSectionOuterCircleSmall = () => {
    setForm((current) => {
      if (!current) return current;
      const nextSectionButtons = {
        ...(current.sectionButtons ?? current.buttons),
        ticketOuterCircleSmallUrl: null,
      };
      return {
        ...current,
        sectionButtons: nextSectionButtons,
        ...(applySectionSettingsToButtonsRef.current
          ? {
              buttons: {
                ...current.buttons,
                ...nextSectionButtons,
              },
            }
          : {}),
      };
    });
  };

  const handleRemoveLogo = () => {
    updateLogoSettings("url", null);
  };

  const outerCircleSmallFitMode =
    form?.buttons?.ticketOuterCircleSmallFitMode ??
    defaultOuterCircleSmallFitMode;
  const outerCircleSmallScaleValue = toFiniteNumber(
    form?.buttons?.ticketOuterCircleSmallScale,
    defaultOuterCircleSmallScale,
  );
  const outerCircleSmallScaleInputValue = toNumberInputValue(
    form?.buttons?.ticketOuterCircleSmallScale,
  );
  const outerCircleSmallPositionXValue = toFiniteNumber(
    form?.buttons?.ticketOuterCircleSmallPositionX,
    defaultOuterCircleSmallPosition,
  );
  const outerCircleSmallPositionXInputValue = toNumberInputValue(
    form?.buttons?.ticketOuterCircleSmallPositionX,
  );
  const outerCircleSmallPositionYValue = toFiniteNumber(
    form?.buttons?.ticketOuterCircleSmallPositionY,
    defaultOuterCircleSmallPosition,
  );
  const outerCircleSmallPositionYInputValue = toNumberInputValue(
    form?.buttons?.ticketOuterCircleSmallPositionY,
  );
  const sectionButtons = form?.sectionButtons ?? form?.buttons;
  const sectionOuterCircleSmallPreview =
    normalizeMediaUrl(sectionButtons?.ticketOuterCircleSmallUrl) ||
    terminalTicketIconActiveLarge;
  const ticketOuterCircleSmallPreview = applySectionSettingsToButtons
    ? sectionOuterCircleSmallPreview
    : normalizeMediaUrl(form?.buttons?.ticketOuterCircleSmallUrl) ||
      terminalTicketIconActiveLarge;
  const sectionOuterCircleSmallFitMode =
    sectionButtons?.ticketOuterCircleSmallFitMode ??
    form?.buttons?.ticketOuterCircleSmallFitMode ??
    defaultOuterCircleSmallFitMode;
  const sectionOuterCircleSmallScaleValue = toFiniteNumber(
    sectionButtons?.ticketOuterCircleSmallScale,
    defaultOuterCircleSmallScale,
  );
  const sectionOuterCircleSmallScaleInputValue = toNumberInputValue(
    sectionButtons?.ticketOuterCircleSmallScale,
  );
  const sectionOuterCircleSmallPositionXValue = toFiniteNumber(
    sectionButtons?.ticketOuterCircleSmallPositionX,
    defaultOuterCircleSmallPosition,
  );
  const sectionOuterCircleSmallPositionXInputValue = toNumberInputValue(
    sectionButtons?.ticketOuterCircleSmallPositionX,
  );
  const sectionOuterCircleSmallPositionYValue = toFiniteNumber(
    sectionButtons?.ticketOuterCircleSmallPositionY,
    defaultOuterCircleSmallPosition,
  );
  const sectionOuterCircleSmallPositionYInputValue = toNumberInputValue(
    sectionButtons?.ticketOuterCircleSmallPositionY,
  );
  const logoPreview =
    normalizeMediaUrl(form?.logo?.url) || tehnoSkyTerminalLogo;
  const logoFitMode = form?.logo?.fitMode ?? defaultLogoFitMode;
  const logoScaleValue = toFiniteNumber(form?.logo?.scale, defaultLogoScale);
  const logoScaleInputValue = toNumberInputValue(form?.logo?.scale);
  const logoPositionXValue = toFiniteNumber(
    form?.logo?.positionX,
    defaultLogoPosition,
  );
  const logoPositionXInputValue = toNumberInputValue(form?.logo?.positionX);
  const logoPositionYValue = toFiniteNumber(
    form?.logo?.positionY,
    defaultLogoPosition,
  );
  const logoPositionYInputValue = toNumberInputValue(form?.logo?.positionY);
  const hasSelectedQueue = filterQueueIds.length > 0;
  const terminalPreviewIframeRef = useRef<HTMLIFrameElement | null>(null);
  const terminalPreviewWrapRef = useRef<HTMLDivElement | null>(null);
  const previewScaleUpdateRef = useRef<(() => void) | null>(null);
  const previewOrientationTouchedRef = useRef(false);
  const [isTerminalPreviewIframeReady, setIsTerminalPreviewIframeReady] =
    useState(false);
  const [previewQueueTerminals, setPreviewQueueTerminals] = useState<
    Array<{
      orderUnitId: number;
      terminalName: string;
      terminalOrientation: "VERTICAL" | "HORIZONTAL" | null;
    }>
  >([]);
  const [previewSelectedOrderUnitId, setPreviewSelectedOrderUnitId] = useState<
    number | null
  >(null);
  const [previewMockMode, setPreviewMockMode] = useState<
    "categories" | "services"
  >("categories");
  const [previewTerminalOrientation, setPreviewTerminalOrientation] = useState<
    "VERTICAL" | "HORIZONTAL"
  >("HORIZONTAL");
  const [previewAspectRatio, setPreviewAspectRatio] = useState<"16:9" | "4:3">(
    "16:9",
  );
  const terminalPreviewSrc = useMemo(
    () => `/terminal-preview?preview=1&v=${Date.now()}`,
    [],
  );

  useEffect(() => {
    (window as any).__terminalPreviewForceUpdate = () => {
      previewScaleUpdateRef.current?.();
    };
    (window as any).__terminalPreviewDump = () => {
      const wrap = terminalPreviewWrapRef.current as HTMLDivElement | null;
      const frame =
        terminalPreviewIframeRef.current as HTMLIFrameElement | null;
      const rect = wrap?.getBoundingClientRect();
      const data = {
        hasWrap: Boolean(wrap),
        hasFrame: Boolean(frame),
        wrapClient: wrap ? { w: wrap.clientWidth, h: wrap.clientHeight } : null,
        wrapRect: rect
          ? { w: rect.width, h: rect.height, top: rect.top, left: rect.left }
          : null,
        frameStyle: frame
          ? {
              w: frame.style.width,
              h: frame.style.height,
              transform: frame.style.transform,
            }
          : null,
        hasUpdateRef: typeof previewScaleUpdateRef.current === "function",
      };
      if ((window as any).__terminalPreviewDebug) {
        // eslint-disable-next-line no-console
        console.log("[terminal-preview] dump(json)", JSON.stringify(data));
      } else {
        // eslint-disable-next-line no-console
        console.log("[terminal-preview] dump", data);
      }
      previewScaleUpdateRef.current?.();
      return data;
    };
    return () => {
      if (typeof (window as any).__terminalPreviewForceUpdate === "function") {
        delete (window as any).__terminalPreviewForceUpdate;
      }
      if (typeof (window as any).__terminalPreviewDump === "function") {
        delete (window as any).__terminalPreviewDump;
      }
    };
  }, []);

  useEffect(() => {
    if (!sectionsExpanded.preview) return;
    if (!form) return;
    if (!isTerminalPreviewIframeReady) return;
    const frame = terminalPreviewIframeRef.current;
    const win = frame?.contentWindow;
    if (!win) return;

    win.postMessage(
      {
        type: "TERMINAL_PREVIEW_APPEARANCE",
        payload: {
          appearance: {
            settings: form,
            backgrounds,
            childrenBackgrounds,
          },
          isChildrenMode: isChildrenModePreviewActive,
        },
      },
      window.location.origin,
    );
  }, [
    backgrounds,
    childrenBackgrounds,
    // Иногда обновления сетки могут менять поля внутри объекта; привязываемся к "сигнатуре",
    // чтобы превью гарантированно реагировало на изменения.
    form?.grid?.columns,
    form?.grid?.gap,
    form?.grid?.rowGap,
    form?.grid?.minCardHeight,
    form?.grid?.maxCardHeight,
    form?.grid?.minCardWidth,
    form?.grid?.maxCardWidth,
    form?.grid?.paddingTop,
    form?.grid?.paddingX,
    form?.sectionGrid?.columns,
    form?.sectionGrid?.gap,
    form?.sectionGrid?.rowGap,
    form?.sectionGrid?.minCardHeight,
    form?.sectionGrid?.maxCardHeight,
    form?.sectionGrid?.minCardWidth,
    form?.sectionGrid?.maxCardWidth,
    form?.sectionGrid?.paddingTop,
    form?.sectionGrid?.paddingX,
    form?.buttons?.gradient,
    form?.buttons?.fontFamily,
    form?.buttons?.fontWeight,
    form?.buttons?.fontSize,
    form?.buttons?.textColor,
    form?.sectionButtons?.gradient,
    form?.sectionButtons?.fontFamily,
    form?.sectionButtons?.fontWeight,
    form?.sectionButtons?.fontSize,
    form?.sectionButtons?.textColor,
    form?.greeting?.isEnabled,
    form?.greeting?.fontSize,
    form?.greeting?.paddingTop,
    form?.greeting?.paddingBottom,
    form?.greeting?.textColor,
    form?.greeting?.title?.ru,
    form?.greeting?.subTitle?.ru,
    form?.header?.backgroundColor,
    form?.header?.textColor,
    form?.header?.fontFamily,
    form?.header?.fontSize,
    form?.logo?.url,
    form?.logo?.fitMode,
    form?.logo?.scale,
    form?.logo?.positionX,
    form?.logo?.positionY,
    form?.backgroundFitMode,
    form?.backgroundOffsetByHeader,
    form?.activeBackgroundId,
    form?.childrenMode?.activateChildrenMode,
    form?.childrenMode?.showToggleButton,
    form?.childrenMode?.decorationType,
    form?.childrenMode?.presetThemeId,
    form?.childrenMode?.showStandardModeIcons,
    form?.childrenMode?.hideIcons,
    form?.childrenMode?.switchByAgeEnabled,
    form?.childrenMode?.ageThreshold,
    form?.childrenMode?.activeBackgroundId,
    form?.childrenMode?.backgroundFitMode,
    form?.childrenMode?.backgroundOffsetByHeader,
    form?.childrenMode?.buttonShape,
    form?.customCss,
    isChildrenModePreviewActive,
    sectionsExpanded.childrenMode,
    sectionsExpanded.preview,
    isTerminalPreviewIframeReady,
  ]);

  useEffect(() => {
    if (!sectionsExpanded.preview) return;
    const branchId = filterBranchIds[0];
    const queueId = filterQueueIds[0];
    if (!branchId || !queueId) return;
    // Услуги в превью — муляжи (без запросов к API).
  }, [filterBranchIds, filterQueueIds, sectionsExpanded.preview]);

  useEffect(() => {
    if (!sectionsExpanded.preview) return;
    const branchId = filterBranchIds[0];
    const queueId = filterQueueIds[0];
    if (!branchId || !queueId) return;

    let cancelled = false;
    previewOrientationTouchedRef.current = false;
    setIsTerminalPreviewIframeReady(false);
    axios
      .get<BranchQueueLinksDto>(
        BranchAPI.queueLinks(branchId, queueId),
        commonHeaders(),
      )
      .then((r) => {
        if (cancelled) return;
        const terminals = (r.data?.terminals ?? []).map((t) => ({
          orderUnitId: Number((t as any).orderUnitId),
          terminalName: String((t as any).terminalName ?? ""),
          terminalOrientation:
            ((t as any).terminalOrientation as
              | "VERTICAL"
              | "HORIZONTAL"
              | null) ?? null,
        }));
        setPreviewQueueTerminals(terminals);

        // Если терминал ещё не выбран — выбираем первый. Если выбранный исчез — тоже первый.
        setPreviewSelectedOrderUnitId((current) => {
          if (
            current != null &&
            terminals.some((t) => t.orderUnitId === current)
          ) {
            return current;
          }
          return terminals[0]?.orderUnitId ?? null;
        });
      })
      .catch(() => {
        if (cancelled) return;
        setPreviewQueueTerminals([]);
        setPreviewSelectedOrderUnitId(null);
      });

    return () => {
      cancelled = true;
    };
  }, [filterBranchIds, filterQueueIds, sectionsExpanded.preview]);

  const previewSelectedTerminalOrientation = useMemo(() => {
    if (previewSelectedOrderUnitId == null) return null;
    return (
      previewQueueTerminals.find(
        (t) => t.orderUnitId === previewSelectedOrderUnitId,
      )?.terminalOrientation ?? null
    );
  }, [previewQueueTerminals, previewSelectedOrderUnitId]);

  useEffect(() => {
    if (previewOrientationTouchedRef.current) return;
    const next =
      previewSelectedTerminalOrientation === "VERTICAL" ||
      previewSelectedTerminalOrientation === "HORIZONTAL"
        ? previewSelectedTerminalOrientation
        : "HORIZONTAL";
    setPreviewTerminalOrientation(next);
  }, [previewSelectedTerminalOrientation]);

  const previewMockOrderUnitsData = useMemo(() => {
    const columnsRaw = (() => {
      // Для категорий используем sectionGrid, для услуг — grid
      if (previewMockMode === "categories") return form?.sectionGrid?.columns;
      return form?.grid?.columns;
    })();
    const columns = Number.isFinite(columnsRaw)
      ? Math.max(1, Math.min(8, Number(columnsRaw)))
      : 3;
    const count = columns * 2; // два ряда

    const topics: OrderUnitsType["categoryView"]["topics"] =
      previewMockMode === "services"
        ? Array.from({ length: count }).map((_, idx) => {
            const n = idx + 1;
            const nameRu = `Услуга ${n}`;
            return {
              id: n,
              isActiveNow: true,
              nameRu,
              nameBe: nameRu,
              nameEn: nameRu,
            };
          })
        : [];

    const categories: OrderUnitsType["categoryView"]["categories"] =
      previewMockMode === "categories"
        ? Array.from({ length: count }).map((_, idx) => {
            const n = idx + 1;
            const nameRu = `Услуга ${n}`;
            // Важно: чтобы NormalMode считал элемент категорией, нужно topics/subCategories непустые
            return {
              id: n,
              nameRu,
              nameBe: nameRu,
              nameEn: nameRu,
              topics: [
                {
                  id: 1000 + n,
                  isActiveNow: true,
                  nameRu: `Подуслуга ${n}`,
                  nameBe: `Подуслуга ${n}`,
                  nameEn: `Subservice ${n}`,
                },
              ],
              subCategories: [],
            };
          })
        : [];

    const terminalId = previewSelectedOrderUnitId ?? 0;
    return {
      id: terminalId,
      name: `preview-terminal-${terminalId}`,
      terminalIp: null,
      categoryView: { topics, categories },
    };
  }, [
    form?.grid?.columns,
    form?.sectionGrid?.columns,
    previewMockMode,
    previewSelectedOrderUnitId,
  ]);

  const previewBaseResolution = useMemo(() => {
    // Базовое "реальное" разрешение, в котором терминал обычно крутится.
    // Превью масштабируем transform'ом, чтобы соблюсти 1:1 размеры.
    const isHorizontal = previewTerminalOrientation === "HORIZONTAL";
    const isWide = previewAspectRatio === "16:9";
    if (isHorizontal) {
      return isWide
        ? { width: 1920, height: 1080 }
        : { width: 1440, height: 1080 };
    }
    // Для вертикали сохраняем тот же выбор ratio: 16:9 -> 9:16, 4:3 -> 3:4
    return isWide
      ? { width: 1080, height: 1920 }
      : { width: 1080, height: 1440 };
  }, [previewAspectRatio, previewTerminalOrientation]);

  const previewAspectRatioText = useMemo(() => {
    const isHorizontal = previewTerminalOrientation === "HORIZONTAL";
    if (previewAspectRatio === "16:9")
      return isHorizontal ? "16 / 9" : "9 / 16";
    return isHorizontal ? "4 / 3" : "3 / 4";
  }, [previewAspectRatio, previewTerminalOrientation]);

  const [previewScale, setPreviewScale] = useState(1);

  useEffect(() => {
    previewScaleUpdateRef.current = () => {
      if (!sectionsExpanded.preview) return;
      const el = terminalPreviewWrapRef.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const w = rect.width || el.clientWidth;
      const h = rect.height || el.clientHeight;

      if (!w || !h) {
        if ((window as any).__terminalPreviewDebug) {
          // eslint-disable-next-line no-console
          console.debug("[terminal-preview] updateRef(EMPTY)", {
            rectW: rect.width,
            rectH: rect.height,
            clientW: el.clientWidth,
            clientH: el.clientHeight,
            base: previewBaseResolution,
          });
        }
        return;
      }

      const sx = w / previewBaseResolution.width;
      const sy = h / previewBaseResolution.height;
      const next = Math.min(sx, sy, 1);
      if ((window as any).__terminalPreviewDebug) {
        // eslint-disable-next-line no-console
        console.debug("[terminal-preview] updateRef", {
          w,
          h,
          sx,
          sy,
          next,
          base: previewBaseResolution,
        });
      }
      setPreviewScale((prev) => (Math.abs(prev - next) < 0.001 ? prev : next));
    };
    return () => {
      if (typeof previewScaleUpdateRef.current === "function") {
        previewScaleUpdateRef.current = null;
      }
    };
  }, [
    previewBaseResolution.height,
    previewBaseResolution.width,
    sectionsExpanded.preview,
  ]);

  useLayoutEffect(() => {
    if (!sectionsExpanded.preview) return;
    const el = terminalPreviewWrapRef.current;
    if (!el) return;

    let retryFrames = 0;
    let retryTimeouts = 0;
    let timeoutId: number | null = null;
    let controlTimeouts: Array<number> = [];
    const update = () => {
      // clientWidth/clientHeight иногда остаются 0 в момент маунта/раскрытия секции,
      // из-за чего previewScale залипает на 1 и выглядит как "зум".
      // getBoundingClientRect стабильнее и учитывает дробные значения.
      const rect = el.getBoundingClientRect();
      // Для точного влезания iframe важно мерить именно внутренний размер (без border),
      // иначе scale может получиться чуть больше и контент начнёт клипаться по краям.
      // При нестандартном zoom браузера clientWidth/clientHeight округляют и дают заметную
      // ошибку масштаба, поэтому используем rect.* как основной источник.
      const w = rect.width || el.clientWidth;
      const h = rect.height || el.clientHeight;
      if (!w || !h) {
        if ((window as any).__terminalPreviewDebug) {
          // eslint-disable-next-line no-console
          console.debug("[terminal-preview] wrap(EMPTY)", {
            rectW: rect.width,
            rectH: rect.height,
            clientW: el.clientWidth,
            clientH: el.clientHeight,
            base: previewBaseResolution,
            retryFrames,
            retryTimeouts,
          });
        }
        // В некоторых браузерах/при первом раскрытии секции размер может “прийти”
        // только через несколько кадров (display/scrollbar/dvh перерасчёт).
        if (retryFrames < 10) {
          retryFrames += 1;
          requestAnimationFrame(update);
        }
        // Бывает, что layout стабилизируется заметно позже (например, из-за dvh),
        // поэтому даём ещё небольшой “хвост” ретраев по таймеру.
        if (retryTimeouts < 30 && timeoutId == null) {
          retryTimeouts += 1;
          timeoutId = window.setTimeout(() => {
            timeoutId = null;
            update();
          }, 50);
        }
        return;
      }
      const sx = w / previewBaseResolution.width;
      const sy = h / previewBaseResolution.height;
      // В превью не увеличиваем терминал выше базового 1:1 масштаба.
      // Иначе при большом контейнере появляется "зум".
      const next = Math.min(sx, sy, 1);
      // Тихая диагностика по флагу (в консоль ничего не пишем без явного включения).
      if ((window as any).__terminalPreviewDebug) {
        // eslint-disable-next-line no-console
        console.debug("[terminal-preview] wrap", {
          w,
          h,
          sx,
          sy,
          next,
          base: previewBaseResolution,
        });
      }
      setPreviewScale((prev) => (Math.abs(prev - next) < 0.001 ? prev : next));
    };

    previewScaleUpdateRef.current = update;

    // Меряем на следующем кадре, когда браузер уже посчитал layout.
    const raf1 = requestAnimationFrame(update);
    const raf2 = requestAnimationFrame(update);

    const ro = new ResizeObserver(() => {
      requestAnimationFrame(update);
    });
    ro.observe(el);

    // При первом показе в некоторых окружениях размеры "досчитываются" только
    // после смены фокуса/visibility или изменения visualViewport (например, dvh).
    const onFocus = () => requestAnimationFrame(update);
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        requestAnimationFrame(update);
        requestAnimationFrame(update);
      }
    };
    const onWindowResize = () => requestAnimationFrame(update);
    window.addEventListener("focus", onFocus);
    window.addEventListener("resize", onWindowResize);
    document.addEventListener("visibilitychange", onVisibility);

    const vv = window.visualViewport;
    const onVv = () => requestAnimationFrame(update);
    vv?.addEventListener("resize", onVv);
    vv?.addEventListener("scroll", onVv);

    // Контрольные замеры после открытия секции: полезно, когда layout стабилизируется
    // позже (например, из-за dvh/scrollbar/шрифтов) и ResizeObserver не триггерится.
    controlTimeouts = [50, 200, 500, 1000].map((ms) =>
      window.setTimeout(() => update(), ms),
    );

    // Шрифты могут подгружаться уже после первого paint и менять layout.
    const fonts: any = (document as any).fonts;
    if (fonts?.ready?.then) {
      fonts.ready.then(() => requestAnimationFrame(update)).catch(() => {});
    }

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      ro.disconnect();
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("resize", onWindowResize);
      document.removeEventListener("visibilitychange", onVisibility);
      vv?.removeEventListener("resize", onVv);
      vv?.removeEventListener("scroll", onVv);
      controlTimeouts.forEach((id) => window.clearTimeout(id));
      controlTimeouts = [];
      if (timeoutId != null) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (previewScaleUpdateRef.current === update) {
        previewScaleUpdateRef.current = null;
      }
    };
  }, [
    previewBaseResolution.height,
    previewBaseResolution.width,
    sectionsExpanded.preview,
  ]);

  useEffect(() => {
    if (!sectionsExpanded.preview) return;
    if (!isTerminalPreviewIframeReady) return;
    const frame = terminalPreviewIframeRef.current;
    const win = frame?.contentWindow;
    if (!win) return;
    win.postMessage(
      {
        type: "TERMINAL_PREVIEW_ORDER_UNITS",
        payload: { orderUnitsData: previewMockOrderUnitsData },
      },
      window.location.origin,
    );
  }, [
    previewMockOrderUnitsData,
    sectionsExpanded.preview,
    isTerminalPreviewIframeReady,
  ]);

  if (!hasSelectedQueue) {
    return (
      <div className={classes.container}>
        <ContentHeader
          name="Терминал"
          filterContent={
            <BranchQueueFilter
              selectedBranchIds={filterBranchIds}
              selectedQueueIds={filterQueueIds}
              onBranchChange={setFilterBranchIds}
              onQueueChange={setFilterQueueIds}
              singleQueue
              queueIdsFilter={queueIdsFilter}
            />
          }
        />
        <div className={classes.section}>
          <p className={classes.noQueueSelectedHint}>
            Выберите очередь в фильтре, чтобы настроить внешний вид терминала.
          </p>
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className={classes.container}>
        <ContentHeader
          name="Терминал"
          filterContent={
            <BranchQueueFilter
              selectedBranchIds={filterBranchIds}
              selectedQueueIds={filterQueueIds}
              onBranchChange={setFilterBranchIds}
              onQueueChange={setFilterQueueIds}
              singleQueue
              queueIdsFilter={queueIdsFilter}
            />
          }
        />
        {isLoadingTerminalAppearance && <div>Загрузка...</div>}
      </div>
    );
  }
  const greetingTextColor = parseColorWithAlpha(
    form?.greeting?.textColor ?? "#000000",
    "#000000",
  );
  const headerBackgroundColor = parseColorWithAlpha(
    form?.header?.backgroundColor ?? defaultHeaderSettings.backgroundColor,
    defaultHeaderSettings.backgroundColor,
  );
  const headerTextColor = parseColorWithAlpha(
    form?.header?.textColor ?? defaultHeaderSettings.textColor,
    defaultHeaderSettings.textColor,
  );
  const sectionGradientStartColor = parseColorWithAlpha(
    sectionButtonGradient.startColor,
    defaultButtonGradient.startColor,
  );
  const sectionGradientEndColor = parseColorWithAlpha(
    sectionButtonGradient.endColor,
    defaultButtonGradient.endColor,
  );
  const sectionButtonTextColor = parseColorWithAlpha(
    sectionButtons?.textColor ?? "#ffffff",
    "#ffffff",
  );
  const buttonGradientStartColor = parseColorWithAlpha(
    buttonGradient.startColor,
    defaultButtonGradient.startColor,
  );
  const buttonGradientEndColor = parseColorWithAlpha(
    buttonGradient.endColor,
    defaultButtonGradient.endColor,
  );
  const buttonTextColor = parseColorWithAlpha(
    form.buttons.textColor ?? "#ffffff",
    "#ffffff",
  );

  return (
    <div className={classes.container}>
      <ContentHeader
        name="Терминал"
        filterContent={
          <BranchQueueFilter
            selectedBranchIds={filterBranchIds}
            selectedQueueIds={filterQueueIds}
            onBranchChange={setFilterBranchIds}
            onQueueChange={setFilterQueueIds}
            singleQueue
            queueIdsFilter={queueIdsFilter}
          />
        }
      />

      {saveBindingModalOpen && (
        <div
          className={classNames(classes.saveBindingModal, {
            [classes.saveBindingModalActive]: saveBindingModalOpen,
          })}
          onClick={() => setSaveBindingModalOpen(false)}
          role="presentation"
        >
          <div
            className={classes.saveBindingModalContainer}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="save-binding-title"
          >
            {/* <h2 id="save-binding-title" className={classes.saveBindingModalTitle}>
              Привязка к филиалам и очередям
            </h2> */}
            <BranchQueueTreeSelect
              label=""
              selectedBranchIds={saveModalBranchIds}
              selectedQueueIds={saveModalQueueIds}
              onBranchChange={setSaveModalBranchIds}
              onQueueChange={setSaveModalQueueIds}
              className={classes.saveBindingTreeSelect}
            />
            <div className={classes.saveBindingModalActions}>
              <Button
                content="Отмена"
                btnStyle="white"
                onClick={() => setSaveBindingModalOpen(false)}
              />
              <Button
                content="Применить"
                btnStyle="blue"
                onClick={handleSaveBindingConfirm}
                isLoading={isUpdateTerminalAppearanceLoading}
                isDisabled={!canEditTerminal || saveModalQueueIds.length === 0}
              />
            </div>
          </div>
        </div>
      )}

      {isLoadingTerminalAppearance && <div>Загрузка...</div>}

      {form && (
        <div
          className={classNames(classes.form, {
            [classes.readOnly]: !canEditTerminal,
          })}
          aria-disabled={!canEditTerminal}
        >
          <section className={classes.section}>
            <button
              type="button"
              className={classes.sectionHeader}
              onClick={() => toggleSectionExpanded("preview")}
            >
              <h3>Превью терминала</h3>
              <span
                className={`${classes.collapseIcon} ${
                  sectionsExpanded.preview ? classes.collapseIconExpanded : ""
                }`}
                aria-hidden="true"
              >
                {sectionsExpanded.preview ? "▲" : "▼"}
              </span>
            </button>
            {sectionsExpanded.preview && (
              <div className={classes.terminalPreviewPanel}>
                {previewQueueTerminals.length > 1 && (
                  <label className={classes.previewTerminalSelectLabel}>
                    Терминал
                    <select
                      value={previewSelectedOrderUnitId ?? ""}
                      onChange={(e) => {
                        const raw = e.target.value;
                        const next =
                          raw && Number.isFinite(Number(raw))
                            ? Number(raw)
                            : null;
                        setPreviewSelectedOrderUnitId(next);
                      }}
                    >
                      {previewQueueTerminals.map((t) => (
                        <option key={t.orderUnitId} value={t.orderUnitId}>
                          {t.terminalName || `Терминал #${t.orderUnitId}`}{" "}
                          {t.terminalOrientation
                            ? `(${t.terminalOrientation === "HORIZONTAL" ? "гор." : "верт."})`
                            : ""}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <label className={classes.previewTerminalSelectLabel}>
                  Ориентация
                  <select
                    value={previewTerminalOrientation}
                    onChange={(e) => {
                      previewOrientationTouchedRef.current = true;
                      setPreviewTerminalOrientation(
                        (e.target.value as "VERTICAL" | "HORIZONTAL") ||
                          "HORIZONTAL",
                      );
                    }}
                  >
                    <option value="HORIZONTAL">Горизонтальная</option>
                    <option value="VERTICAL">Вертикальная</option>
                  </select>
                </label>
                <label className={classes.previewTerminalSelectLabel}>
                  Соотношение сторон
                  <select
                    value={previewAspectRatio}
                    onChange={(e) => {
                      setPreviewAspectRatio(
                        (e.target.value as "16:9" | "4:3") || "16:9",
                      );
                    }}
                  >
                    <option value="16:9">16:9</option>
                    <option value="4:3">4:3</option>
                  </select>
                </label>

                <label className={classes.previewTerminalSelectLabel}>
                  Режим превью
                  <select
                    value={previewMockMode}
                    onChange={(e) =>
                      setPreviewMockMode(
                        e.target.value === "services"
                          ? "services"
                          : "categories",
                      )
                    }
                  >
                    <option value="categories">Категории (sectionGrid)</option>
                    <option value="services">Услуги (grid)</option>
                  </select>
                </label>

                {isChildrenModePreviewActive && (
                  <p className={classes.hintText}>
                    Превью отображается в детском режиме при редактировании
                    секции «Детский режим» или при включённой принудительной
                    активации.
                  </p>
                )}

                <div
                  ref={terminalPreviewWrapRef}
                  className={classNames(classes.terminalPreviewFrameWrap, {
                    [classes.terminalPreviewFrameWrapHorizontal]:
                      previewTerminalOrientation === "HORIZONTAL",
                    [classes.terminalPreviewFrameWrapHorizontal4x3]:
                      previewTerminalOrientation === "HORIZONTAL" &&
                      previewAspectRatio === "4:3",
                    [classes.terminalPreviewFrameWrapVertical3x4]:
                      previewTerminalOrientation === "VERTICAL" &&
                      previewAspectRatio === "4:3",
                  })}
                >
                  <iframe
                    title="Превью терминала"
                    className={classes.terminalPreviewFrame}
                    ref={terminalPreviewIframeRef}
                    onLoad={() => {
                      setIsTerminalPreviewIframeReady(true);
                      // После загрузки iframe размеры могут “досчитаться” только на следующий кадр,
                      // поэтому дополнительно пересчитываем scale, чтобы не ждать первого клика.
                      requestAnimationFrame(() =>
                        previewScaleUpdateRef.current?.(),
                      );
                      requestAnimationFrame(() =>
                        previewScaleUpdateRef.current?.(),
                      );
                    }}
                    src={terminalPreviewSrc}
                    sandbox="allow-scripts allow-same-origin"
                    style={{
                      width: `${previewBaseResolution.width}px`,
                      height: `${previewBaseResolution.height}px`,
                      transform: `scale(${previewScale})`,
                      transformOrigin: "0 0",
                    }}
                  />
                </div>
              </div>
            )}
          </section>

          <section className={classes.section}>
            <button
              type="button"
              className={classes.sectionHeader}
              onClick={() => toggleSectionExpanded("categoriesGrid")}
            >
              <h3>Сетка категорий</h3>
              <span
                className={`${classes.collapseIcon} ${
                  sectionsExpanded.categoriesGrid
                    ? classes.collapseIconExpanded
                    : ""
                }`}
                aria-hidden="true"
              >
                {sectionsExpanded.categoriesGrid ? "▲" : "▼"}
              </span>
            </button>
            {sectionsExpanded.categoriesGrid && (
              <div className={classes.grid}>
                <label>
                  Колонки
                  <input
                    type="number"
                    min={1}
                    value={toNumberInputValue(form.sectionGrid?.columns)}
                    onChange={(event) =>
                      updateSectionGrid(
                        "columns",
                        parseNumberInput(event.target.value),
                      )
                    }
                  />
                </label>
                <label>
                  Отступы между карточками
                  <input
                    type="number"
                    value={toNumberInputValue(form.sectionGrid?.gap)}
                    onChange={(event) =>
                      updateSectionGrid(
                        "gap",
                        parseNumberInput(event.target.value),
                      )
                    }
                  />
                </label>
                <label>
                  Отступ между рядами
                  <input
                    type="number"
                    value={toNumberInputValue(form.sectionGrid?.rowGap)}
                    onChange={(event) =>
                      updateSectionGrid(
                        "rowGap",
                        parseNumberInput(event.target.value),
                      )
                    }
                  />
                </label>
                <label>
                  Высота карточки
                  <input
                    type="number"
                    value={sectionCardHeightValue}
                    onChange={(event) =>
                      updateSectionGridRange(
                        "minCardHeight",
                        "maxCardHeight",
                        parseNumberInput(event.target.value),
                      )
                    }
                  />
                </label>
                <label>
                  Ширина карточки
                  <input
                    type="number"
                    value={sectionCardWidthValue}
                    onChange={(event) =>
                      updateSectionGridRange(
                        "minCardWidth",
                        "maxCardWidth",
                        parseNumberInput(event.target.value),
                      )
                    }
                  />
                </label>
                <label>
                  Верхний отступ
                  <input
                    type="number"
                    value={toNumberInputValue(form.sectionGrid?.paddingTop)}
                    onChange={(event) =>
                      updateSectionGrid(
                        "paddingTop",
                        parseNumberInput(event.target.value),
                      )
                    }
                  />
                </label>
              </div>
            )}
          </section>

          <section className={classes.section}>
            <button
              type="button"
              className={classes.sectionHeader}
              onClick={() => toggleSectionExpanded("servicesGrid")}
            >
              <h3>Сетка услуг</h3>
              <span
                className={`${classes.collapseIcon} ${
                  sectionsExpanded.servicesGrid
                    ? classes.collapseIconExpanded
                    : ""
                }`}
                aria-hidden="true"
              >
                {sectionsExpanded.servicesGrid ? "▲" : "▼"}
              </span>
            </button>
            {sectionsExpanded.servicesGrid && (
              <div className={classes.grid}>
                <label>
                  Колонки
                  <input
                    type="number"
                    min={1}
                    value={toNumberInputValue(form.grid.columns)}
                    onChange={(event) =>
                      updateGrid(
                        "columns",
                        parseNumberInput(event.target.value),
                      )
                    }
                  />
                </label>
                <label>
                  Отступы между карточками
                  <input
                    type="number"
                    value={toNumberInputValue(form.grid.gap)}
                    onChange={(event) =>
                      updateGrid("gap", parseNumberInput(event.target.value))
                    }
                  />
                </label>
                <label>
                  Отступ между рядами
                  <input
                    type="number"
                    value={toNumberInputValue(form.grid.rowGap)}
                    onChange={(event) =>
                      updateGrid("rowGap", parseNumberInput(event.target.value))
                    }
                  />
                </label>
                <label>
                  Высота карточки
                  <input
                    type="number"
                    value={cardHeightValue}
                    onChange={(event) =>
                      updateGridRange(
                        "minCardHeight",
                        "maxCardHeight",
                        parseNumberInput(event.target.value),
                      )
                    }
                  />
                </label>
                <label>
                  Ширина карточки
                  <input
                    type="number"
                    value={cardWidthValue}
                    onChange={(event) =>
                      updateGridRange(
                        "minCardWidth",
                        "maxCardWidth",
                        parseNumberInput(event.target.value),
                      )
                    }
                  />
                </label>
                <label>
                  Верхний отступ
                  <input
                    type="number"
                    value={toNumberInputValue(form.grid.paddingTop)}
                    onChange={(event) =>
                      updateGrid(
                        "paddingTop",
                        parseNumberInput(event.target.value),
                      )
                    }
                  />
                </label>
                {/* <label style={{ color: 'red' }}>
                Горизонтальные отступы
                <input
                  type="number"
                  value={toNumberInputValue(form.grid.paddingX)}
                  onChange={(event) =>
                    updateGrid('paddingX', parseNumberInput(event.target.value))
                  }
                />
              </label> */}
              </div>
            )}
          </section>

          <section className={classes.section}>
            <button
              type="button"
              className={classes.sectionHeader}
              onClick={() => toggleSectionExpanded("background")}
            >
              <h3>Фон терминала (jpg, jpeg, png, webp, gif, svg, mp4, webm)</h3>
              <span
                className={`${classes.collapseIcon} ${
                  sectionsExpanded.background
                    ? classes.collapseIconExpanded
                    : ""
                }`}
                aria-hidden="true"
              >
                {sectionsExpanded.background ? "▲" : "▼"}
              </span>
            </button>
            {sectionsExpanded.background && (
              <>
                <div className={classes.buttonMediaUpload}>
                  <label>
                    Изображение фона
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml,video/mp4,video/webm,video/ogg,video/x-matroska"
                      onChange={handleUploadBackground}
                    />
                    {isUploadTerminalBackgroundLoading && (
                      <span>Загрузка...</span>
                    )}
                  </label>
                </div>
                <div className={classes.buttonMediaControls}>
                  <label className={classes.mediaControlLabel}>
                    Режим отображения
                    <select
                      className={classes.displayModeSelect}
                      value={form.backgroundFitMode ?? defaultBackgroundFitMode}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          backgroundFitMode: event.target
                            .value as TerminalAppearanceSettings["backgroundFitMode"],
                        })
                      }
                    >
                      <option value="contain">Вписать в область</option>
                      <option value="cover">Заполнить область</option>
                      <option value="stretch">
                        Растянуть (по ширине и высоте)
                      </option>
                      <option value="width">Растянуть по ширине</option>
                      <option value="height">Растянуть по высоте</option>
                      <option value="none">Оригинальный размер</option>
                    </select>
                  </label>
                </div>

                <div className={classes.backgroundList}>
                  {backgrounds.map((background) => (
                    <div key={background.id} className={classes.backgroundItem}>
                      <label className={classes.backgroundHeader}>
                        <input
                          type="radio"
                          name="activeBackground"
                          checked={activeBackgroundId === background.id}
                          onChange={() =>
                            setForm({
                              ...form,
                              activeBackgroundId: background.id,
                            })
                          }
                        />
                        <span>{background.fileName}</span>
                      </label>
                      <div className={classes.backgroundPreview}>
                        {background.mediaType === "IMAGE" ? (
                          <img src={background.url} alt={background.fileName} />
                        ) : (
                          <video src={background.url} muted />
                        )}
                      </div>
                      <div className={classes.backgroundActions}>
                        <CustomButton
                          content="Удалить"
                          styleType="default"
                          onClick={() =>
                            deleteTerminalBackground(background.id)
                          }
                        />
                      </div>
                    </div>
                  ))}
                  {isDeleteTerminalBackgroundLoading && (
                    <span>Удаление...</span>
                  )}
                </div>
                <div style={{ marginTop: "16px" }}>
                  <label className={classes.checkboxRow}>
                    <input
                      type="checkbox"
                      checked={form.backgroundOffsetByHeader ?? false}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          backgroundOffsetByHeader: event.target.checked,
                        })
                      }
                    />
                    Смещать фон на высоту шапки
                  </label>
                </div>
              </>
            )}
          </section>

          <section className={classes.section}>
            <button
              type="button"
              className={classes.sectionHeader}
              onClick={() => toggleSectionExpanded("childrenMode")}
            >
              <h3>Детский режим</h3>
              <span
                className={`${classes.collapseIcon} ${
                  sectionsExpanded.childrenMode
                    ? classes.collapseIconExpanded
                    : ""
                }`}
                aria-hidden="true"
              >
                {sectionsExpanded.childrenMode ? "▲" : "▼"}
              </span>
            </button>
            {sectionsExpanded.childrenMode && (
              <>
                <h4 className={classes.subsectionTitle}>Сценарии активации</h4>
                <label className={classes.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={form.childrenMode?.activateChildrenMode ?? false}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        childrenMode: {
                          ...defaultChildrenModeSettings,
                          ...(form.childrenMode ?? {}),
                          activateChildrenMode: event.target.checked,
                        },
                      })
                    }
                  />
                  Активировать детский режим
                </label>
                <p className={classes.hintText}>
                  Терминал постоянно работает в детском режиме. Переключение
                  произойдёт после завершения текущей сессии посетителя, если
                  терминал занят.
                </p>

                <label className={classes.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={form.childrenMode?.showToggleButton ?? false}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        childrenMode: {
                          ...defaultChildrenModeSettings,
                          ...(form.childrenMode ?? {}),
                          showToggleButton: event.target.checked,
                        },
                      })
                    }
                  />
                  Отображать кнопку детского режима на терминале
                </label>
                <p className={classes.hintText}>
                  Кнопка появляется на главном экране и переключает между
                  обычным и детским режимом. Скрывается, если включена
                  принудительная активация.
                </p>

                <h4 className={classes.subsectionTitle}>Оформление</h4>
                <p className={classes.hintText}>
                  Превью терминала автоматически переключается в детский режим
                  при изменении темы, индивидуального оформления и
                  пользовательских настроек.
                </p>
                <div className={classes.buttonMediaControls}>
                  <label className={classes.mediaControlLabel}>
                    Способ оформления
                    <select
                      className={classes.displayModeSelect}
                      value={form.childrenMode?.decorationType ?? "THEME"}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          childrenMode: {
                            ...defaultChildrenModeSettings,
                            ...(form.childrenMode ?? {}),
                            decorationType: event.target
                              .value as TerminalChildrenModeDecorationType,
                          },
                        })
                      }
                    >
                      <option value="THEME">Тема</option>
                      <option value="INDIVIDUAL">Индивидуальная тема</option>
                    </select>
                  </label>
                </div>

                {(form.childrenMode?.decorationType ?? "THEME") === "THEME" && (
                  <>
                    <h4 className={classes.subsectionTitle}>Тема</h4>
                    <div className={classes.backgroundList}>
                      {CHILDREN_MODE_PRESET_THEMES.map((theme) => (
                        <label
                          key={theme.id}
                          className={classNames(
                            classes.backgroundItem,
                            classes.themeOption,
                          )}
                        >
                          <div className={classes.backgroundHeader}>
                            <input
                              type="radio"
                              name="childrenPresetTheme"
                              checked={
                                (form.childrenMode?.presetThemeId ??
                                  DEFAULT_CHILDREN_PRESET_THEME_ID) === theme.id
                              }
                              onChange={() =>
                                setForm({
                                  ...form,
                                  childrenMode: {
                                    ...defaultChildrenModeSettings,
                                    ...(form.childrenMode ?? {}),
                                    presetThemeId: theme.id,
                                  },
                                })
                              }
                            />
                            <span>{theme.name}</span>
                          </div>
                          <p className={classes.hintText}>
                            {theme.description}
                          </p>
                          <div
                            className={classes.themePreviewSwatch}
                            style={{
                              backgroundColor: theme.terminalBackgroundColor,
                            }}
                          />
                        </label>
                      ))}
                    </div>
                  </>
                )}

                {(form.childrenMode?.decorationType ?? "THEME") ===
                  "INDIVIDUAL" && (
                  <>
                    <h4 className={classes.subsectionTitle}>
                      Индивидуальная тема
                    </h4>
                    <h4 className={classes.subsectionTitle}>
                      Фон терминала (jpg, jpeg, png, webp, gif, svg, mp4, webm)
                    </h4>
                    <div className={classes.buttonMediaUpload}>
                      <label>
                        Изображение фона
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml,video/mp4,video/webm,video/ogg,video/x-matroska"
                          onChange={handleUploadChildrenBackground}
                        />
                        {isUploadTerminalChildrenBackgroundLoading && (
                          <span>Загрузка...</span>
                        )}
                      </label>
                    </div>
                    <div className={classes.buttonMediaControls}>
                      <label className={classes.mediaControlLabel}>
                        Режим отображения
                        <select
                          className={classes.displayModeSelect}
                          value={
                            form.childrenMode?.backgroundFitMode ??
                            defaultBackgroundFitMode
                          }
                          onChange={(event) =>
                            setForm({
                              ...form,
                              childrenMode: {
                                ...defaultChildrenModeSettings,
                                ...(form.childrenMode ?? {}),
                                backgroundFitMode: event.target
                                  .value as NonNullable<
                                  TerminalAppearanceSettings["backgroundFitMode"]
                                >,
                              },
                            })
                          }
                        >
                          <option value="contain">Вписать в область</option>
                          <option value="cover">Заполнить область</option>
                          <option value="stretch">
                            Растянуть (по ширине и высоте)
                          </option>
                          <option value="width">Растянуть по ширине</option>
                          <option value="height">Растянуть по высоте</option>
                          <option value="none">Оригинальный размер</option>
                        </select>
                      </label>
                    </div>

                    <div className={classes.backgroundList}>
                      {childrenBackgrounds.map((background) => (
                        <div
                          key={background.id}
                          className={classes.backgroundItem}
                        >
                          <label className={classes.backgroundHeader}>
                            <input
                              type="radio"
                              name="activeChildrenBackground"
                              checked={
                                activeChildrenBackgroundId === background.id
                              }
                              onChange={() =>
                                setForm({
                                  ...form,
                                  childrenMode: {
                                    ...defaultChildrenModeSettings,
                                    ...(form.childrenMode ?? {}),
                                    activeBackgroundId: background.id,
                                  },
                                })
                              }
                            />
                            <span>{background.fileName}</span>
                          </label>
                          <div className={classes.backgroundPreview}>
                            {background.mediaType === "IMAGE" ? (
                              <img
                                src={background.url}
                                alt={background.fileName}
                              />
                            ) : (
                              <video src={background.url} muted />
                            )}
                          </div>
                          <div className={classes.backgroundActions}>
                            <CustomButton
                              content="Удалить"
                              styleType="default"
                              onClick={() =>
                                deleteTerminalChildrenBackground(background.id)
                              }
                            />
                          </div>
                        </div>
                      ))}
                      {isDeleteTerminalChildrenBackgroundLoading && (
                        <span>Удаление...</span>
                      )}
                    </div>
                    <div style={{ marginTop: "16px" }}>
                      <label className={classes.checkboxRow}>
                        <input
                          type="checkbox"
                          checked={
                            form.childrenMode?.backgroundOffsetByHeader ?? false
                          }
                          onChange={(event) =>
                            setForm({
                              ...form,
                              childrenMode: {
                                ...defaultChildrenModeSettings,
                                ...(form.childrenMode ?? {}),
                                backgroundOffsetByHeader: event.target.checked,
                              },
                            })
                          }
                        />
                        Смещать фон на высоту шапки
                      </label>
                    </div>

                    <h4 className={classes.subsectionTitle}>Форма кнопок</h4>
                    <div className={classes.buttonMediaControls}>
                      <label className={classes.mediaControlLabel}>
                        Форма плиток
                        <select
                          className={classes.displayModeSelect}
                          value={form.childrenMode?.buttonShape ?? "RECTANGLE"}
                          onChange={(event) =>
                            setForm({
                              ...form,
                              childrenMode: {
                                ...defaultChildrenModeSettings,
                                ...(form.childrenMode ?? {}),
                                buttonShape: event.target
                                  .value as TerminalChildrenModeButtonShape,
                              },
                            })
                          }
                        >
                          <option value="RECTANGLE">Прямоугольник</option>
                          <option value="CIRCLE">Круг</option>
                          <option value="CLOUD">Облако</option>
                        </select>
                      </label>
                    </div>
                    <p className={classes.hintText}>
                      Если файл фона не загружен, используется белый фон. Цвета
                      плиток назначаются случайно из яркой палитры.
                    </p>
                  </>
                )}

                <h4 className={classes.subsectionTitle}>
                  Пользовательские настройки
                </h4>
                <label className={classes.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={form.childrenMode?.showStandardModeIcons ?? false}
                    disabled={form.childrenMode?.hideIcons ?? false}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        childrenMode: {
                          ...defaultChildrenModeSettings,
                          ...(form.childrenMode ?? {}),
                          showStandardModeIcons: event.target.checked,
                        },
                      })
                    }
                  />
                  Отображать иконки стандартного режима
                </label>
                <label className={classes.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={form.childrenMode?.hideIcons ?? false}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        childrenMode: {
                          ...defaultChildrenModeSettings,
                          ...(form.childrenMode ?? {}),
                          hideIcons: event.target.checked,
                          showStandardModeIcons: event.target.checked
                            ? false
                            : (form.childrenMode?.showStandardModeIcons ??
                              false),
                        },
                      })
                    }
                  />
                  Не показывать иконку
                </label>
                <p className={classes.hintText}>
                  Сетка категорий, сетка услуг и размеры шрифтов настраиваются в
                  соответствующих секциях этой страницы и применяются к детскому
                  режиму.
                </p>

                <h4 className={classes.subsectionTitle}>
                  Автопереключение по возрасту
                </h4>
                <label className={classes.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={form.childrenMode?.switchByAgeEnabled ?? false}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        childrenMode: {
                          ...defaultChildrenModeSettings,
                          ...(form.childrenMode ?? {}),
                          switchByAgeEnabled: event.target.checked,
                        },
                      })
                    }
                  />
                  Переключение в детский режим по определённому возрасту
                </label>
                {form.childrenMode?.switchByAgeEnabled && (
                  <label className={classes.mediaControlLabel}>
                    Возраст
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      className={classes.displayModeSelect}
                      value={toNumberInputValue(
                        form.childrenMode?.ageThreshold,
                      )}
                      onChange={(event) => {
                        const raw = event.target.value;
                        if (raw === "") {
                          setForm({
                            ...form,
                            childrenMode: {
                              ...defaultChildrenModeSettings,
                              ...(form.childrenMode ?? {}),
                              ageThreshold: undefined,
                            },
                          });
                          return;
                        }
                        const parsed = parseBoundedIntegerInput(raw, 1, 99);
                        if (parsed === null) {
                          return;
                        }
                        setForm({
                          ...form,
                          childrenMode: {
                            ...defaultChildrenModeSettings,
                            ...(form.childrenMode ?? {}),
                            ageThreshold: parsed,
                          },
                        });
                      }}
                      onBlur={(event) => {
                        const parsed = parseBoundedIntegerInput(
                          event.target.value,
                          1,
                          99,
                        );
                        if (parsed !== null) {
                          return;
                        }
                        setForm({
                          ...form,
                          childrenMode: {
                            ...defaultChildrenModeSettings,
                            ...(form.childrenMode ?? {}),
                            ageThreshold: 14,
                          },
                        });
                      }}
                    />
                  </label>
                )}
                {form.childrenMode?.switchByAgeEnabled && (
                  <p className={classes.hintText}>
                    Камера терминала определяет возраст посетителя. Если возраст
                    меньше указанного, детский режим включается автоматически.
                  </p>
                )}
              </>
            )}
          </section>

          <section className={classes.section}>
            <button
              type="button"
              className={classes.sectionHeader}
              onClick={() => toggleSectionExpanded("autoReturnHome")}
            >
              <h3>Автовозврат на главную</h3>
              <span
                className={`${classes.collapseIcon} ${
                  sectionsExpanded.autoReturnHome
                    ? classes.collapseIconExpanded
                    : ""
                }`}
                aria-hidden="true"
              >
                {sectionsExpanded.autoReturnHome ? "▲" : "▼"}
              </span>
            </button>
            {sectionsExpanded.autoReturnHome && (
              <>
                <label className={classes.mediaControlLabel}>
                  Возврат по истечению времени бездействия посетителя (сек)
                  <input
                    type="number"
                    className={classes.displayModeSelect}
                    min={0}
                    step={1}
                    value={toNumberInputValue(form.autoReturnHome?.idleSeconds)}
                    onChange={(event) => {
                      const parsed = parseNumberInput(event.target.value);
                      setForm({
                        ...form,
                        autoReturnHome: {
                          ...defaultAutoReturnHomeSettings,
                          ...(form.autoReturnHome ?? {}),
                          idleSeconds: Number.isFinite(parsed)
                            ? Math.max(0, parsed)
                            : defaultAutoReturnHomeSettings.idleSeconds,
                        },
                      });
                    }}
                  />
                </label>
                <p className={classes.hintText}>
                  Если терминал не на главной странице и нет кликов или касаний
                  указанное количество секунд — возврат на начальную страницу. 0
                  — автовозврат отключён.
                </p>
              </>
            )}
          </section>

          <section className={classes.section}>
            <button
              type="button"
              className={classes.sectionHeader}
              onClick={() => toggleSectionExpanded("logo")}
            >
              <h3>Лого (jpg, jpeg, png, webp, gif, svg)</h3>
              <span
                className={`${classes.collapseIcon} ${
                  sectionsExpanded.logo ? classes.collapseIconExpanded : ""
                }`}
                aria-hidden="true"
              >
                {sectionsExpanded.logo ? "▲" : "▼"}
              </span>
            </button>
            {sectionsExpanded.logo && (
              <div className={classes.buttonMediaUpload}>
                <div className={classes.logoUploadRow}>
                  <label htmlFor="terminal-logo-upload">
                    Изображение логотипа
                  </label>
                  <div className={classes.logoUploadInputRow}>
                    <input
                      id="terminal-logo-upload"
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                      onChange={handleUploadLogo}
                    />
                    {logoPreview && (
                      <CustomButton
                        content="Удалить"
                        styleType="default"
                        onClick={handleRemoveLogo}
                      />
                    )}
                  </div>
                </div>
                {isUploadTerminalLogoLoading && <span>Загрузка...</span>}
                <div className={classes.buttonMediaControls}>
                  <label>
                    Режим отображения
                    <select
                      className={classes.displayModeSelect}
                      value={logoFitMode}
                      onChange={(event) =>
                        updateLogoSettings("fitMode", event.target.value)
                      }
                    >
                      <option value="scale">Свой размер</option>
                      <option value="contain">Вписать в область</option>
                      <option value="cover">Заполнить область</option>
                      <option value="stretch">
                        Растянуть (по ширине и высоте)
                      </option>
                      <option value="width">Растянуть по ширине</option>
                      <option value="height">Растянуть по высоте</option>
                      <option value="none">Оригинальный размер</option>
                    </select>
                  </label>
                  <label className={classes.rangeField}>
                    Масштаб
                    <div className={classes.rangeRow}>
                      <input
                        type="range"
                        min={50}
                        max={200}
                        step={1}
                        value={logoScaleValue}
                        onChange={(event) =>
                          updateLogoSettings(
                            "scale",
                            Number(event.target.value),
                          )
                        }
                        disabled={logoFitMode !== "scale"}
                      />
                      <input
                        type="number"
                        min={50}
                        max={200}
                        value={logoScaleInputValue}
                        onChange={(event) =>
                          updateLogoSettings(
                            "scale",
                            parseNumberInput(event.target.value),
                          )
                        }
                        disabled={logoFitMode !== "scale"}
                      />
                      <span>%</span>
                    </div>
                  </label>
                  <label className={classes.rangeField}>
                    Позиция по X
                    <div className={classes.rangeRow}>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        step={1}
                        value={logoPositionXValue}
                        onChange={(event) =>
                          updateLogoSettings(
                            "positionX",
                            Number(event.target.value),
                          )
                        }
                      />
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={logoPositionXInputValue}
                        onChange={(event) =>
                          updateLogoSettings(
                            "positionX",
                            parseNumberInput(event.target.value),
                          )
                        }
                      />
                      <span>%</span>
                    </div>
                  </label>
                  <label className={classes.rangeField}>
                    Позиция по Y
                    <div className={classes.rangeRow}>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        step={1}
                        value={logoPositionYValue}
                        onChange={(event) =>
                          updateLogoSettings(
                            "positionY",
                            Number(event.target.value),
                          )
                        }
                      />
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={logoPositionYInputValue}
                        onChange={(event) =>
                          updateLogoSettings(
                            "positionY",
                            parseNumberInput(event.target.value),
                          )
                        }
                      />
                      <span>%</span>
                    </div>
                  </label>
                </div>
                {logoPreview && (
                  <div className={classes.logoMediaCard}>
                    <div className={classes.logoMediaPreview}>
                      <div
                        className={classes.logoMediaPreviewImage}
                        style={{
                          backgroundImage: `url(${logoPreview})`,
                          backgroundSize: getMediaBackgroundSize(
                            logoFitMode,
                            logoScaleValue,
                          ),
                          backgroundPosition: `${logoPositionXValue}% ${logoPositionYValue}%`,
                        }}
                        aria-label="Предпросмотр логотипа"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>

          <section className={classes.section}>
            <button
              type="button"
              className={classes.sectionHeader}
              onClick={() => toggleSectionExpanded("greeting")}
            >
              <h3>Приветственное сообщение</h3>
              <span
                className={`${classes.collapseIcon} ${
                  sectionsExpanded.greeting ? classes.collapseIconExpanded : ""
                }`}
                aria-hidden="true"
              >
                {sectionsExpanded.greeting ? "▲" : "▼"}
              </span>
            </button>
            {sectionsExpanded.greeting && (
              <div className={classes.localizedGrid}>
                <div className={classes.greetingRow}>
                  <label>
                    Заголовок (ru)
                    <input
                      value={form.greeting.title.ru}
                      onChange={(event) =>
                        updateGreetingText("title", "ru", event.target.value)
                      }
                    />
                  </label>
                  <label>
                    Заголовок (by)
                    <input
                      value={form.greeting.title.by}
                      onChange={(event) =>
                        updateGreetingText("title", "by", event.target.value)
                      }
                    />
                  </label>
                  <label>
                    Заголовок (en)
                    <input
                      value={form.greeting.title.en}
                      onChange={(event) =>
                        updateGreetingText("title", "en", event.target.value)
                      }
                    />
                  </label>
                </div>
                <div className={classes.greetingRow}>
                  <label>
                    Подзаголовок (ru)
                    <input
                      value={form.greeting.subTitle.ru}
                      onChange={(event) =>
                        updateGreetingText("subTitle", "ru", event.target.value)
                      }
                    />
                  </label>
                  <label>
                    Подзаголовок (by)
                    <input
                      value={form.greeting.subTitle.by}
                      onChange={(event) =>
                        updateGreetingText("subTitle", "by", event.target.value)
                      }
                    />
                  </label>
                  <label>
                    Подзаголовок (en)
                    <input
                      value={form.greeting.subTitle.en}
                      onChange={(event) =>
                        updateGreetingText("subTitle", "en", event.target.value)
                      }
                    />
                  </label>
                </div>
                <div className={classes.greetingRowWide}>
                  <label>
                    Размер текста
                    <input
                      type="number"
                      value={toNumberInputValue(form.greeting.fontSize)}
                      onChange={(event) =>
                        updateGreetingSetting(
                          "fontSize",
                          parseNumberInput(event.target.value),
                        )
                      }
                    />
                  </label>
                  <label>
                    Отступ сверху
                    <input
                      type="number"
                      value={toNumberInputValue(form.greeting.paddingTop)}
                      onChange={(event) =>
                        updateGreetingSetting(
                          "paddingTop",
                          parseNumberInput(event.target.value),
                        )
                      }
                    />
                  </label>
                  <label>
                    Отступ снизу
                    <input
                      type="number"
                      value={toNumberInputValue(form.greeting.paddingBottom)}
                      onChange={(event) =>
                        updateGreetingSetting(
                          "paddingBottom",
                          parseNumberInput(event.target.value),
                        )
                      }
                    />
                  </label>
                  <label className={classes.checkboxRow}>
                    <input
                      type="checkbox"
                      checked={form.greeting.isEnabled ?? true}
                      onChange={(event) =>
                        updateGreetingFlag("isEnabled", event.target.checked)
                      }
                    />
                    Показывать приветственное сообщение
                  </label>
                  <div className={classes.gradientField}>
                    <span>Цвет текста</span>
                    <input
                      type="color"
                      value={greetingTextColor.hex}
                      onChange={(event) =>
                        updateGreetingColor(
                          buildColorWithAlpha(
                            event.target.value,
                            greetingTextColor.alpha,
                          ),
                        )
                      }
                    />
                    <span>Непрозрачность</span>
                    <div className={classes.rangeRow}>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        step={1}
                        value={Math.round(greetingTextColor.alpha * 100)}
                        onChange={(event) =>
                          updateGreetingColor(
                            buildColorWithAlpha(
                              greetingTextColor.hex,
                              clampNumber(Number(event.target.value), 0, 100) /
                                100,
                            ),
                          )
                        }
                      />
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={Math.round(greetingTextColor.alpha * 100)}
                        onChange={(event) =>
                          updateGreetingColor(
                            buildColorWithAlpha(
                              greetingTextColor.hex,
                              clampNumber(Number(event.target.value), 0, 100) /
                                100,
                            ),
                          )
                        }
                      />
                      <span>%</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>

          <section className={classes.section}>
            <button
              type="button"
              className={classes.sectionHeader}
              onClick={() => toggleSectionExpanded("header")}
            >
              <h3>Шапка терминала</h3>
              <span
                className={`${classes.collapseIcon} ${
                  sectionsExpanded.header ? classes.collapseIconExpanded : ""
                }`}
                aria-hidden="true"
              >
                {sectionsExpanded.header ? "▲" : "▼"}
              </span>
            </button>
            {sectionsExpanded.header && (
              <div className={classes.grid}>
                <div className={classes.gradientField}>
                  <span>Цвет фона</span>
                  <input
                    type="color"
                    value={headerBackgroundColor.hex}
                    onChange={(event) =>
                      updateHeaderSetting(
                        "backgroundColor",
                        buildColorWithAlpha(
                          event.target.value,
                          headerBackgroundColor.alpha,
                        ),
                      )
                    }
                  />
                  <span>Непрозрачность</span>
                  <div className={classes.rangeRow}>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={1}
                      value={Math.round(headerBackgroundColor.alpha * 100)}
                      onChange={(event) =>
                        updateHeaderSetting(
                          "backgroundColor",
                          buildColorWithAlpha(
                            headerBackgroundColor.hex,
                            clampNumber(Number(event.target.value), 0, 100) /
                              100,
                          ),
                        )
                      }
                    />
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={Math.round(headerBackgroundColor.alpha * 100)}
                      onChange={(event) =>
                        updateHeaderSetting(
                          "backgroundColor",
                          buildColorWithAlpha(
                            headerBackgroundColor.hex,
                            clampNumber(Number(event.target.value), 0, 100) /
                              100,
                          ),
                        )
                      }
                    />
                    <span>%</span>
                  </div>
                </div>
                <div className={classes.gradientField}>
                  <span>Цвет текста</span>
                  <input
                    type="color"
                    value={headerTextColor.hex}
                    onChange={(event) =>
                      updateHeaderSetting(
                        "textColor",
                        buildColorWithAlpha(
                          event.target.value,
                          headerTextColor.alpha,
                        ),
                      )
                    }
                  />
                  <span>Непрозрачность</span>
                  <div className={classes.rangeRow}>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={1}
                      value={Math.round(headerTextColor.alpha * 100)}
                      onChange={(event) =>
                        updateHeaderSetting(
                          "textColor",
                          buildColorWithAlpha(
                            headerTextColor.hex,
                            clampNumber(Number(event.target.value), 0, 100) /
                              100,
                          ),
                        )
                      }
                    />
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={Math.round(headerTextColor.alpha * 100)}
                      onChange={(event) =>
                        updateHeaderSetting(
                          "textColor",
                          buildColorWithAlpha(
                            headerTextColor.hex,
                            clampNumber(Number(event.target.value), 0, 100) /
                              100,
                          ),
                        )
                      }
                    />
                    <span>%</span>
                  </div>
                </div>
                <label>
                  Шрифт
                  <select
                    value={
                      form.header?.fontFamily ??
                      defaultHeaderSettings.fontFamily
                    }
                    onChange={(event) =>
                      updateHeaderSetting("fontFamily", event.target.value)
                    }
                  >
                    {fontOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Размер шрифта
                  <input
                    type="number"
                    min={10}
                    max={72}
                    value={toNumberInputValue(form.header?.fontSize)}
                    onChange={(event) =>
                      updateHeaderSetting(
                        "fontSize",
                        parseNumberInput(event.target.value),
                      )
                    }
                  />
                </label>
              </div>
            )}
          </section>

          <section className={classes.section}>
            <button
              type="button"
              className={classes.sectionHeader}
              onClick={() => toggleSectionExpanded("categoryButtons")}
            >
              <h3>Кнопки категорий</h3>
              <span
                className={`${classes.collapseIcon} ${
                  sectionsExpanded.categoryButtons
                    ? classes.collapseIconExpanded
                    : ""
                }`}
                aria-hidden="true"
              >
                {sectionsExpanded.categoryButtons ? "▲" : "▼"}
              </span>
            </button>
            {sectionsExpanded.categoryButtons && (
              <>
                <div className={classes.buttonSettingsGroups}>
                  <div className={classes.buttonSettingsGroup}>
                    <button
                      type="button"
                      className={classes.buttonSettingsHeader}
                      onClick={() => toggleSectionButtonGroup("icon")}
                    >
                      <span className={classes.buttonSettingsTitle}>
                        Иконка кнопки
                      </span>
                      <span
                        className={`${classes.collapseIcon} ${
                          sectionButtonGroupsExpanded.icon
                            ? classes.collapseIconExpanded
                            : ""
                        }`}
                        aria-hidden="true"
                      >
                        {sectionButtonGroupsExpanded.icon ? "▲" : "▼"}
                      </span>
                    </button>
                    {sectionButtonGroupsExpanded.icon && (
                      <div className={classes.buttonSettingsBody}>
                        <div className={classes.buttonMediaUpload}>
                          <div className={classes.logoUploadRow}>
                            <label htmlFor="section-outer-circle-small-upload">
                              Иконка (jpg, jpeg, png, webp, gif, svg)
                            </label>
                            <div className={classes.logoUploadInputRow}>
                              <input
                                id="section-outer-circle-small-upload"
                                type="file"
                                accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                                onChange={handleUploadSectionOuterCircleSmall}
                              />
                              {sectionOuterCircleSmallPreview && (
                                <CustomButton
                                  content="Удалить"
                                  styleType="default"
                                  onClick={handleRemoveSectionOuterCircleSmall}
                                />
                              )}
                            </div>
                          </div>
                          {isUploadSectionOuterCircleSmallLoading && (
                            <span>Загрузка...</span>
                          )}
                          <div className={classes.buttonMediaControls}>
                            <label>
                              Режим отображения
                              <select
                                className={classes.displayModeSelect}
                                value={sectionOuterCircleSmallFitMode}
                                onChange={(event) =>
                                  updateSectionButtonSettings(
                                    "ticketOuterCircleSmallFitMode",
                                    event.target.value,
                                  )
                                }
                              >
                                <option value="scale">Свой размер</option>
                                <option value="contain">
                                  Вписать в область
                                </option>
                                <option value="cover">Заполнить область</option>
                                <option value="stretch">
                                  Растянуть (по ширине и высоте)
                                </option>
                                <option value="width">
                                  Растянуть по ширине
                                </option>
                                <option value="height">
                                  Растянуть по высоте
                                </option>
                                <option value="none">
                                  Оригинальный размер
                                </option>
                              </select>
                            </label>
                            <label className={classes.rangeField}>
                              Масштаб
                              <div className={classes.rangeRow}>
                                <input
                                  type="range"
                                  min={50}
                                  max={200}
                                  step={1}
                                  value={sectionOuterCircleSmallScaleValue}
                                  onChange={(event) =>
                                    updateSectionButtonSettings(
                                      "ticketOuterCircleSmallScale",
                                      Number(event.target.value),
                                    )
                                  }
                                  disabled={
                                    sectionOuterCircleSmallFitMode !== "scale"
                                  }
                                />
                                <input
                                  type="number"
                                  min={50}
                                  max={200}
                                  value={sectionOuterCircleSmallScaleInputValue}
                                  onChange={(event) =>
                                    updateSectionButtonSettings(
                                      "ticketOuterCircleSmallScale",
                                      parseNumberInput(event.target.value),
                                    )
                                  }
                                  disabled={
                                    sectionOuterCircleSmallFitMode !== "scale"
                                  }
                                />
                                <span>%</span>
                              </div>
                            </label>
                            <label className={classes.rangeField}>
                              Позиция по X
                              <div className={classes.rangeRow}>
                                <input
                                  type="range"
                                  min={0}
                                  max={100}
                                  step={1}
                                  value={sectionOuterCircleSmallPositionXValue}
                                  onChange={(event) =>
                                    updateSectionButtonSettings(
                                      "ticketOuterCircleSmallPositionX",
                                      Number(event.target.value),
                                    )
                                  }
                                />
                                <input
                                  type="number"
                                  min={0}
                                  max={100}
                                  value={
                                    sectionOuterCircleSmallPositionXInputValue
                                  }
                                  onChange={(event) =>
                                    updateSectionButtonSettings(
                                      "ticketOuterCircleSmallPositionX",
                                      parseNumberInput(event.target.value),
                                    )
                                  }
                                />
                                <span>%</span>
                              </div>
                            </label>
                            <label className={classes.rangeField}>
                              Позиция по Y
                              <div className={classes.rangeRow}>
                                <input
                                  type="range"
                                  min={0}
                                  max={100}
                                  step={1}
                                  value={sectionOuterCircleSmallPositionYValue}
                                  onChange={(event) =>
                                    updateSectionButtonSettings(
                                      "ticketOuterCircleSmallPositionY",
                                      Number(event.target.value),
                                    )
                                  }
                                />
                                <input
                                  type="number"
                                  min={0}
                                  max={100}
                                  value={
                                    sectionOuterCircleSmallPositionYInputValue
                                  }
                                  onChange={(event) =>
                                    updateSectionButtonSettings(
                                      "ticketOuterCircleSmallPositionY",
                                      parseNumberInput(event.target.value),
                                    )
                                  }
                                />
                                <span>%</span>
                              </div>
                            </label>
                          </div>
                          {sectionOuterCircleSmallPreview && (
                            <div className={classes.buttonMediaCard}>
                              <div className={classes.buttonMediaPreview}>
                                <div
                                  className={classes.buttonMediaPreviewImage}
                                  style={{
                                    backgroundImage: `url(${sectionOuterCircleSmallPreview})`,
                                    backgroundSize: getMediaBackgroundSize(
                                      sectionOuterCircleSmallFitMode,
                                      sectionOuterCircleSmallScaleValue,
                                    ),
                                    backgroundPosition: `${sectionOuterCircleSmallPositionXValue}% ${sectionOuterCircleSmallPositionYValue}%`,
                                  }}
                                  aria-label="Предпросмотр"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                        <label className={classes.checkboxRow}>
                          <input
                            type="checkbox"
                            checked={
                              sectionButtons?.hideOuterCircleSmall ?? false
                            }
                            onChange={(event) =>
                              updateSectionButtonSettings(
                                "hideOuterCircleSmall",
                                event.target.checked,
                              )
                            }
                          />
                          Скрыть
                        </label>
                      </div>
                    )}
                  </div>
                  <div className={classes.buttonSettingsGroup}>
                    <button
                      type="button"
                      className={classes.buttonSettingsHeader}
                      onClick={() => toggleSectionButtonGroup("fill")}
                    >
                      <span className={classes.buttonSettingsTitle}>
                        Заливка кнопки
                      </span>
                      <span
                        className={`${classes.collapseIcon} ${
                          sectionButtonGroupsExpanded.fill
                            ? classes.collapseIconExpanded
                            : ""
                        }`}
                        aria-hidden="true"
                      >
                        {sectionButtonGroupsExpanded.fill ? "▲" : "▼"}
                      </span>
                    </button>
                    {sectionButtonGroupsExpanded.fill && (
                      <div className={classes.buttonSettingsBody}>
                        <div className={classes.gradientControlGroup}>
                          <span className={classes.gradientLabel}>
                            Градиент заливки
                          </span>
                          <div className={classes.gradientRow}>
                            <div className={classes.gradientField}>
                              <span>Цвет 1</span>
                              <input
                                type="color"
                                value={sectionGradientStartColor.hex}
                                onChange={(event) =>
                                  updateSectionButtonGradient({
                                    startColor: buildColorWithAlpha(
                                      event.target.value,
                                      sectionGradientStartColor.alpha,
                                    ),
                                  })
                                }
                              />
                              <span>Непрозрачность</span>
                              <div className={classes.rangeRow}>
                                <input
                                  type="range"
                                  min={0}
                                  max={100}
                                  step={1}
                                  value={Math.round(
                                    sectionGradientStartColor.alpha * 100,
                                  )}
                                  onChange={(event) =>
                                    updateSectionButtonGradient({
                                      startColor: buildColorWithAlpha(
                                        sectionGradientStartColor.hex,
                                        clampNumber(
                                          Number(event.target.value),
                                          0,
                                          100,
                                        ) / 100,
                                      ),
                                    })
                                  }
                                />
                                <input
                                  type="number"
                                  min={0}
                                  max={100}
                                  value={Math.round(
                                    sectionGradientStartColor.alpha * 100,
                                  )}
                                  onChange={(event) =>
                                    updateSectionButtonGradient({
                                      startColor: buildColorWithAlpha(
                                        sectionGradientStartColor.hex,
                                        clampNumber(
                                          Number(event.target.value),
                                          0,
                                          100,
                                        ) / 100,
                                      ),
                                    })
                                  }
                                />
                                <span>%</span>
                              </div>
                            </div>
                            <div className={classes.gradientField}>
                              <span>Цвет 2</span>
                              <input
                                type="color"
                                value={sectionGradientEndColor.hex}
                                onChange={(event) =>
                                  updateSectionButtonGradient({
                                    endColor: buildColorWithAlpha(
                                      event.target.value,
                                      sectionGradientEndColor.alpha,
                                    ),
                                  })
                                }
                              />
                              <span>Непрозрачность</span>
                              <div className={classes.rangeRow}>
                                <input
                                  type="range"
                                  min={0}
                                  max={100}
                                  step={1}
                                  value={Math.round(
                                    sectionGradientEndColor.alpha * 100,
                                  )}
                                  onChange={(event) =>
                                    updateSectionButtonGradient({
                                      endColor: buildColorWithAlpha(
                                        sectionGradientEndColor.hex,
                                        clampNumber(
                                          Number(event.target.value),
                                          0,
                                          100,
                                        ) / 100,
                                      ),
                                    })
                                  }
                                />
                                <input
                                  type="number"
                                  min={0}
                                  max={100}
                                  value={Math.round(
                                    sectionGradientEndColor.alpha * 100,
                                  )}
                                  onChange={(event) =>
                                    updateSectionButtonGradient({
                                      endColor: buildColorWithAlpha(
                                        sectionGradientEndColor.hex,
                                        clampNumber(
                                          Number(event.target.value),
                                          0,
                                          100,
                                        ) / 100,
                                      ),
                                    })
                                  }
                                />
                                <span>%</span>
                              </div>
                            </div>
                            <label className={classes.gradientField}>
                              Угол
                              <div className={classes.gradientAngle}>
                                <input
                                  type="range"
                                  min={0}
                                  max={360}
                                  step={1}
                                  value={sectionButtonGradient.angle}
                                  onChange={(event) =>
                                    updateSectionButtonGradient({
                                      angle: Number(event.target.value),
                                    })
                                  }
                                />
                                <span className={classes.gradientAngleValue}>
                                  {sectionButtonGradient.angle}°
                                </span>
                              </div>
                            </label>
                          </div>
                          <div
                            className={classes.gradientPreview}
                            style={{ background: sectionButtons?.gradient }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className={classes.buttonSettingsGroup}>
                    <button
                      type="button"
                      className={classes.buttonSettingsHeader}
                      onClick={() => toggleSectionButtonGroup("font")}
                    >
                      <span className={classes.buttonSettingsTitle}>
                        Шрифт кнопки
                      </span>
                      <span
                        className={`${classes.collapseIcon} ${
                          sectionButtonGroupsExpanded.font
                            ? classes.collapseIconExpanded
                            : ""
                        }`}
                        aria-hidden="true"
                      >
                        {sectionButtonGroupsExpanded.font ? "▲" : "▼"}
                      </span>
                    </button>
                    {sectionButtonGroupsExpanded.font && (
                      <div className={classes.buttonSettingsBody}>
                        <div className={classes.grid}>
                          <div className={classes.gradientField}>
                            <span>Цвет текста</span>
                            <input
                              type="color"
                              value={sectionButtonTextColor.hex}
                              onChange={(event) =>
                                updateSectionButtonSettings(
                                  "textColor",
                                  buildColorWithAlpha(
                                    event.target.value,
                                    sectionButtonTextColor.alpha,
                                  ),
                                )
                              }
                            />
                            <span>Непрозрачность</span>
                            <div className={classes.rangeRow}>
                              <input
                                type="range"
                                min={0}
                                max={100}
                                step={1}
                                value={Math.round(
                                  sectionButtonTextColor.alpha * 100,
                                )}
                                onChange={(event) =>
                                  updateSectionButtonSettings(
                                    "textColor",
                                    buildColorWithAlpha(
                                      sectionButtonTextColor.hex,
                                      clampNumber(
                                        Number(event.target.value),
                                        0,
                                        100,
                                      ) / 100,
                                    ),
                                  )
                                }
                              />
                              <input
                                type="number"
                                min={0}
                                max={100}
                                value={Math.round(
                                  sectionButtonTextColor.alpha * 100,
                                )}
                                onChange={(event) =>
                                  updateSectionButtonSettings(
                                    "textColor",
                                    buildColorWithAlpha(
                                      sectionButtonTextColor.hex,
                                      clampNumber(
                                        Number(event.target.value),
                                        0,
                                        100,
                                      ) / 100,
                                    ),
                                  )
                                }
                              />
                              <span>%</span>
                            </div>
                          </div>
                          <label>
                            Шрифт
                            <select
                              value={
                                sectionButtons?.fontFamily ??
                                form.buttons.fontFamily
                              }
                              onChange={(event) =>
                                updateSectionButtonSettings(
                                  "fontFamily",
                                  event.target.value,
                                )
                              }
                            >
                              {fontOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label>
                            Начертание
                            <select
                              value={
                                sectionButtons?.fontWeight ??
                                form.buttons.fontWeight
                              }
                              onChange={(event) =>
                                updateSectionButtonSettings(
                                  "fontWeight",
                                  event.target.value,
                                )
                              }
                            >
                              {fontWeights.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label>
                            Размер шрифта
                            <input
                              type="number"
                              value={toNumberInputValue(
                                sectionButtons?.fontSize,
                              )}
                              onChange={(event) =>
                                updateSectionButtonSettings(
                                  "fontSize",
                                  parseNumberInput(event.target.value),
                                )
                              }
                            />
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ marginTop: "16px" }}>
                  <label className={classes.checkboxRow}>
                    <input
                      type="checkbox"
                      checked={applySectionSettingsToButtons}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        setApplySectionSettingsToButtons(checked);
                        if (checked) {
                          applySectionButtonsToServices();
                        }
                      }}
                    />
                    Применить настройки к кнопкам услуг
                  </label>
                </div>
              </>
            )}
          </section>

          <section className={classes.section}>
            <button
              type="button"
              className={classes.sectionHeader}
              onClick={() => toggleSectionExpanded("serviceButtons")}
            >
              <h3>Кнопки услуг</h3>
              <span
                className={`${classes.collapseIcon} ${
                  sectionsExpanded.serviceButtons
                    ? classes.collapseIconExpanded
                    : ""
                }`}
                aria-hidden="true"
              >
                {sectionsExpanded.serviceButtons ? "▲" : "▼"}
              </span>
            </button>
            {sectionsExpanded.serviceButtons && (
              <div className={classes.buttonSettingsGroups}>
                <div className={classes.buttonSettingsGroup}>
                  <button
                    type="button"
                    className={classes.buttonSettingsHeader}
                    onClick={() => toggleButtonGroup("icon")}
                  >
                    <span className={classes.buttonSettingsTitle}>
                      Иконка кнопки
                    </span>
                    <span
                      className={`${classes.collapseIcon} ${
                        buttonGroupsExpanded.icon
                          ? classes.collapseIconExpanded
                          : ""
                      }`}
                      aria-hidden="true"
                    >
                      {buttonGroupsExpanded.icon ? "▲" : "▼"}
                    </span>
                  </button>
                  {buttonGroupsExpanded.icon && (
                    <div className={classes.buttonSettingsBody}>
                      <div className={classes.buttonMediaUpload}>
                        <div className={classes.logoUploadRow}>
                          <label htmlFor="ticket-outer-circle-small-upload">
                            Иконка(jpg, jpeg, png, webp, gif, svg)
                          </label>
                          <div className={classes.logoUploadInputRow}>
                            <input
                              id="ticket-outer-circle-small-upload"
                              type="file"
                              accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                              onChange={handleUploadOuterCircleSmall}
                            />
                            {ticketOuterCircleSmallPreview && (
                              <CustomButton
                                content="Удалить"
                                styleType="default"
                                onClick={handleRemoveOuterCircleSmall}
                              />
                            )}
                          </div>
                        </div>
                        {isUploadOuterCircleSmallLoading && (
                          <span>Загрузка...</span>
                        )}
                        <div className={classes.buttonMediaControls}>
                          <label>
                            Режим отображения
                            <select
                              className={classes.displayModeSelect}
                              value={outerCircleSmallFitMode}
                              onChange={(event) =>
                                updateButtonSettings(
                                  "ticketOuterCircleSmallFitMode",
                                  event.target.value,
                                )
                              }
                            >
                              <option value="scale">Свой размер</option>
                              <option value="contain">Вписать в область</option>
                              <option value="cover">Заполнить область</option>
                              <option value="stretch">
                                Растянуть (по ширине и высоте)
                              </option>
                              <option value="width">Растянуть по ширине</option>
                              <option value="height">
                                Растянуть по высоте
                              </option>
                              <option value="none">Оригинальный размер</option>
                            </select>
                          </label>
                          <label className={classes.rangeField}>
                            Масштаб
                            <div className={classes.rangeRow}>
                              <input
                                type="range"
                                min={50}
                                max={200}
                                step={1}
                                value={outerCircleSmallScaleValue}
                                onChange={(event) =>
                                  updateButtonSettings(
                                    "ticketOuterCircleSmallScale",
                                    Number(event.target.value),
                                  )
                                }
                                disabled={outerCircleSmallFitMode !== "scale"}
                              />
                              <input
                                type="number"
                                min={50}
                                max={200}
                                value={outerCircleSmallScaleInputValue}
                                onChange={(event) =>
                                  updateButtonSettings(
                                    "ticketOuterCircleSmallScale",
                                    parseNumberInput(event.target.value),
                                  )
                                }
                                disabled={outerCircleSmallFitMode !== "scale"}
                              />
                              <span>%</span>
                            </div>
                          </label>
                          <label className={classes.rangeField}>
                            Позиция по X
                            <div className={classes.rangeRow}>
                              <input
                                type="range"
                                min={0}
                                max={100}
                                step={1}
                                value={outerCircleSmallPositionXValue}
                                onChange={(event) =>
                                  updateButtonSettings(
                                    "ticketOuterCircleSmallPositionX",
                                    Number(event.target.value),
                                  )
                                }
                              />
                              <input
                                type="number"
                                min={0}
                                max={100}
                                value={outerCircleSmallPositionXInputValue}
                                onChange={(event) =>
                                  updateButtonSettings(
                                    "ticketOuterCircleSmallPositionX",
                                    parseNumberInput(event.target.value),
                                  )
                                }
                              />
                              <span>%</span>
                            </div>
                          </label>
                          <label className={classes.rangeField}>
                            Позиция по Y
                            <div className={classes.rangeRow}>
                              <input
                                type="range"
                                min={0}
                                max={100}
                                step={1}
                                value={outerCircleSmallPositionYValue}
                                onChange={(event) =>
                                  updateButtonSettings(
                                    "ticketOuterCircleSmallPositionY",
                                    Number(event.target.value),
                                  )
                                }
                              />
                              <input
                                type="number"
                                min={0}
                                max={100}
                                value={outerCircleSmallPositionYInputValue}
                                onChange={(event) =>
                                  updateButtonSettings(
                                    "ticketOuterCircleSmallPositionY",
                                    parseNumberInput(event.target.value),
                                  )
                                }
                              />
                              <span>%</span>
                            </div>
                          </label>
                        </div>
                        {ticketOuterCircleSmallPreview && (
                          <div className={classes.buttonMediaCard}>
                            <div className={classes.buttonMediaPreview}>
                              <div
                                className={classes.buttonMediaPreviewImage}
                                style={{
                                  backgroundImage: `url(${ticketOuterCircleSmallPreview})`,
                                  backgroundSize: getMediaBackgroundSize(
                                    outerCircleSmallFitMode,
                                    outerCircleSmallScaleValue,
                                  ),
                                  backgroundPosition: `${outerCircleSmallPositionXValue}% ${outerCircleSmallPositionYValue}%`,
                                }}
                                aria-label="Предпросмотр"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      <label className={classes.checkboxRow}>
                        <input
                          type="checkbox"
                          checked={form.buttons.hideOuterCircleSmall}
                          onChange={(event) =>
                            updateButtonSettings(
                              "hideOuterCircleSmall",
                              event.target.checked,
                            )
                          }
                        />
                        Скрыть
                      </label>
                    </div>
                  )}
                </div>
                <div className={classes.buttonSettingsGroup}>
                  <button
                    type="button"
                    className={classes.buttonSettingsHeader}
                    onClick={() => toggleButtonGroup("fill")}
                  >
                    <span className={classes.buttonSettingsTitle}>
                      Заливка кнопки
                    </span>
                    <span
                      className={`${classes.collapseIcon} ${
                        buttonGroupsExpanded.fill
                          ? classes.collapseIconExpanded
                          : ""
                      }`}
                      aria-hidden="true"
                    >
                      {buttonGroupsExpanded.fill ? "▲" : "▼"}
                    </span>
                  </button>
                  {buttonGroupsExpanded.fill && (
                    <div className={classes.buttonSettingsBody}>
                      <div className={classes.gradientControlGroup}>
                        <span className={classes.gradientLabel}>
                          Градиент заливки
                        </span>
                        <div className={classes.gradientRow}>
                          <div className={classes.gradientField}>
                            <span>Цвет 1</span>
                            <input
                              type="color"
                              value={buttonGradientStartColor.hex}
                              onChange={(event) =>
                                updateButtonGradient({
                                  startColor: buildColorWithAlpha(
                                    event.target.value,
                                    buttonGradientStartColor.alpha,
                                  ),
                                })
                              }
                            />
                            <span>Непрозрачность</span>
                            <div className={classes.rangeRow}>
                              <input
                                type="range"
                                min={0}
                                max={100}
                                step={1}
                                value={Math.round(
                                  buttonGradientStartColor.alpha * 100,
                                )}
                                onChange={(event) =>
                                  updateButtonGradient({
                                    startColor: buildColorWithAlpha(
                                      buttonGradientStartColor.hex,
                                      clampNumber(
                                        Number(event.target.value),
                                        0,
                                        100,
                                      ) / 100,
                                    ),
                                  })
                                }
                              />
                              <input
                                type="number"
                                min={0}
                                max={100}
                                value={Math.round(
                                  buttonGradientStartColor.alpha * 100,
                                )}
                                onChange={(event) =>
                                  updateButtonGradient({
                                    startColor: buildColorWithAlpha(
                                      buttonGradientStartColor.hex,
                                      clampNumber(
                                        Number(event.target.value),
                                        0,
                                        100,
                                      ) / 100,
                                    ),
                                  })
                                }
                              />
                              <span>%</span>
                            </div>
                          </div>
                          <div className={classes.gradientField}>
                            <span>Цвет 2</span>
                            <input
                              type="color"
                              value={buttonGradientEndColor.hex}
                              onChange={(event) =>
                                updateButtonGradient({
                                  endColor: buildColorWithAlpha(
                                    event.target.value,
                                    buttonGradientEndColor.alpha,
                                  ),
                                })
                              }
                            />
                            <span>Непрозрачность</span>
                            <div className={classes.rangeRow}>
                              <input
                                type="range"
                                min={0}
                                max={100}
                                step={1}
                                value={Math.round(
                                  buttonGradientEndColor.alpha * 100,
                                )}
                                onChange={(event) =>
                                  updateButtonGradient({
                                    endColor: buildColorWithAlpha(
                                      buttonGradientEndColor.hex,
                                      clampNumber(
                                        Number(event.target.value),
                                        0,
                                        100,
                                      ) / 100,
                                    ),
                                  })
                                }
                              />
                              <input
                                type="number"
                                min={0}
                                max={100}
                                value={Math.round(
                                  buttonGradientEndColor.alpha * 100,
                                )}
                                onChange={(event) =>
                                  updateButtonGradient({
                                    endColor: buildColorWithAlpha(
                                      buttonGradientEndColor.hex,
                                      clampNumber(
                                        Number(event.target.value),
                                        0,
                                        100,
                                      ) / 100,
                                    ),
                                  })
                                }
                              />
                              <span>%</span>
                            </div>
                          </div>
                          <label className={classes.gradientField}>
                            Угол
                            <div className={classes.gradientAngle}>
                              <input
                                type="range"
                                min={0}
                                max={360}
                                step={1}
                                value={buttonGradient.angle}
                                onChange={(event) =>
                                  updateButtonGradient({
                                    angle: Number(event.target.value),
                                  })
                                }
                              />
                              <span className={classes.gradientAngleValue}>
                                {buttonGradient.angle}°
                              </span>
                            </div>
                          </label>
                        </div>
                        <div
                          className={classes.gradientPreview}
                          style={{ background: form.buttons.gradient }}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className={classes.buttonSettingsGroup}>
                  <button
                    type="button"
                    className={classes.buttonSettingsHeader}
                    onClick={() => toggleButtonGroup("font")}
                  >
                    <span className={classes.buttonSettingsTitle}>
                      Шрифт кнопки
                    </span>
                    <span
                      className={`${classes.collapseIcon} ${
                        buttonGroupsExpanded.font
                          ? classes.collapseIconExpanded
                          : ""
                      }`}
                      aria-hidden="true"
                    >
                      {buttonGroupsExpanded.font ? "▲" : "▼"}
                    </span>
                  </button>
                  {buttonGroupsExpanded.font && (
                    <div className={classes.buttonSettingsBody}>
                      <div className={classes.grid}>
                        <div className={classes.gradientField}>
                          <span>Цвет текста</span>
                          <input
                            type="color"
                            value={buttonTextColor.hex}
                            onChange={(event) =>
                              updateButtonSettings(
                                "textColor",
                                buildColorWithAlpha(
                                  event.target.value,
                                  buttonTextColor.alpha,
                                ),
                              )
                            }
                          />
                          <span>Непрозрачность</span>
                          <div className={classes.rangeRow}>
                            <input
                              type="range"
                              min={0}
                              max={100}
                              step={1}
                              value={Math.round(buttonTextColor.alpha * 100)}
                              onChange={(event) =>
                                updateButtonSettings(
                                  "textColor",
                                  buildColorWithAlpha(
                                    buttonTextColor.hex,
                                    clampNumber(
                                      Number(event.target.value),
                                      0,
                                      100,
                                    ) / 100,
                                  ),
                                )
                              }
                            />
                            <input
                              type="number"
                              min={0}
                              max={100}
                              value={Math.round(buttonTextColor.alpha * 100)}
                              onChange={(event) =>
                                updateButtonSettings(
                                  "textColor",
                                  buildColorWithAlpha(
                                    buttonTextColor.hex,
                                    clampNumber(
                                      Number(event.target.value),
                                      0,
                                      100,
                                    ) / 100,
                                  ),
                                )
                              }
                            />
                            <span>%</span>
                          </div>
                        </div>
                        <label>
                          Шрифт
                          <select
                            value={form.buttons.fontFamily}
                            onChange={(event) =>
                              updateButtonSettings(
                                "fontFamily",
                                event.target.value,
                              )
                            }
                          >
                            {fontOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          Начертание
                          <select
                            value={form.buttons.fontWeight}
                            onChange={(event) =>
                              updateButtonSettings(
                                "fontWeight",
                                event.target.value,
                              )
                            }
                          >
                            {fontWeights.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          Размер шрифта
                          <input
                            type="number"
                            value={toNumberInputValue(form.buttons.fontSize)}
                            onChange={(event) =>
                              updateButtonSettings(
                                "fontSize",
                                parseNumberInput(event.target.value),
                              )
                            }
                          />
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </section>

          <section className={classes.section}>
            <button
              type="button"
              className={classes.sectionHeader}
              onClick={() => toggleSectionExpanded("ticket")}
            >
              <h3>Талон</h3>
              <span
                className={`${classes.collapseIcon} ${
                  sectionsExpanded.ticket ? classes.collapseIconExpanded : ""
                }`}
                aria-hidden="true"
              >
                {sectionsExpanded.ticket ? "▲" : "▼"}
              </span>
            </button>
            {sectionsExpanded.ticket && (
              <TicketConstructor
                ticket={form.ticket}
                pictures={[...ticketLayoutImages, ...ticketPictures].filter(
                  (picture, index, list) =>
                    list.findIndex((item) => item.id === picture.id) === index,
                )}
                queueName={selectedQueueName}
                disabled={!canEditTerminal}
                onChange={(nextTicket) =>
                  setForm({
                    ...form,
                    ticket: nextTicket,
                  })
                }
                onPictureUploaded={(picture) => {
                  setTicketPictures((current) =>
                    current.some((item) => item.id === picture.id)
                      ? current
                      : [...current, picture],
                  );
                  setTicketLayoutImages((current) =>
                    current.some((item) => item.id === picture.id)
                      ? current
                      : [...current, picture],
                  );
                }}
              />
            )}
          </section>

          <section className={classes.section}>
            <button
              type="button"
              className={classes.sectionHeader}
              onClick={() => toggleSectionExpanded("ticketWithImage")}
            >
              <h3>Талон с картинкой (jpg, jpeg, png, webp, gif)</h3>
              <span
                className={`${classes.collapseIcon} ${
                  sectionsExpanded.ticketWithImage
                    ? classes.collapseIconExpanded
                    : ""
                }`}
                aria-hidden="true"
              >
                {sectionsExpanded.ticketWithImage ? "▲" : "▼"}
              </span>
            </button>
            {sectionsExpanded.ticketWithImage && (
              <>
                <div style={{ marginBottom: "16px" }}>
                  <label className={classes.checkboxRow}>
                    <input
                      type="checkbox"
                      checked={form.ticketWithImage?.isEnabled ?? false}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          ticketWithImage: {
                            ...defaultTicketWithImageSettings,
                            ...(form.ticketWithImage ?? {}),
                            isEnabled: event.target.checked,
                          },
                        })
                      }
                    />
                    Включить
                  </label>
                </div>
                <div className={classes.buttonMediaUpload}>
                  <label>
                    Картинка для печати
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handleUploadTicketPicture}
                    />
                    {isUploadTerminalTicketPictureLoading && (
                      <span>Загрузка...</span>
                    )}
                  </label>
                </div>
                <div className={classes.backgroundList}>
                  {ticketPictures.map((picture) => (
                    <div key={picture.id} className={classes.backgroundItem}>
                      <div className={classes.backgroundHeader}>
                        <span>{picture.fileName}</span>
                      </div>
                      <div className={classes.backgroundPreview}>
                        <img src={picture.url} alt={picture.fileName} />
                      </div>
                      <div className={classes.backgroundActions}>
                        <CustomButton
                          content="Удалить"
                          styleType="default"
                          onClick={() =>
                            deleteTerminalTicketPicture(picture.id)
                          }
                        />
                      </div>
                    </div>
                  ))}
                  {isDeleteTerminalTicketPictureLoading && (
                    <span>Удаление...</span>
                  )}
                </div>
              </>
            )}
          </section>

          <section className={classes.section}>
            <button
              type="button"
              className={classes.sectionHeader}
              onClick={() => toggleSectionExpanded("customCss")}
            >
              <h3>Пользовательский CSS</h3>
              <span
                className={`${classes.collapseIcon} ${
                  sectionsExpanded.customCss ? classes.collapseIconExpanded : ""
                }`}
                aria-hidden="true"
              >
                {sectionsExpanded.customCss ? "▲" : "▼"}
              </span>
            </button>
            {sectionsExpanded.customCss && (
              <textarea
                className={classes.cssEditor}
                value={form.customCss}
                onChange={(event) =>
                  setForm({ ...form, customCss: event.target.value })
                }
                placeholder="Введите CSS-правила. Они будут применены с максимальным приоритетом."
              />
            )}
          </section>

          <div className={classes.actions}>
            <CustomButton
              content="Сбросить"
              styleType="default"
              onClick={handleReset}
            />
            <CustomButton
              content={
                isUpdateTerminalAppearanceLoading
                  ? "Сохранение..."
                  : "Сохранить"
              }
              styleType="active"
              onClick={handleSaveClick}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default TerminalAppearancePage;
